// The turn machine.
//
// `advance` runs the game forward until it needs a decision, `legalActions`
// enumerates exactly what may be decided, and `applyAction` takes one. Nothing
// outside the enumerated list is ever accepted, which is what lets the same
// engine drive a UI and a search-based AI without either of them being able to
// cheat.
//
// The structure follows the rules closely enough that the rule numbers are
// worth keeping in view:
//
//   315  Awaken / Beginning / Channel / Draw
//   316  Main, where everything discretionary happens
//   317  Ending, which expires "this turn" effects
//   318  Cleanup, re-run until the board stops changing
//   327  the Chain, resolved by FEPR (337-340)
//   463  Combat: showdown, damage, resolution

import { isSpellType, isUnitType, isPermanentType } from './cards.ts';
import type {
  Ability, ActivationCost, Instruction, ObjectFilter, Selector, Trigger,
} from './effects.ts';
import * as ops from './ops.ts';
import {
  candidates, conditionHolds, NeedsChoice, runEffects, type EffectContext,
} from './resolve.ts';
import { shuffle } from './rng.ts';
import type { Rules } from './state.ts';
import {
  OPPONENT, abilitiesOf, abilitiesOn, abilityResourceCost, activePassives, additionalCostDiscount, baseLocation, battlefield, battlefieldLocation, boardObjects, canBeDealtDamage, canPay, cantBeMovedBy, choosingDiscount, chosenDiscount, deflectCost, discardCandidates, effectiveMight, emptyPool, extraCostDiscount, facedownAt, firstEachTurnDiscount, hasKeywordNow, hasLethalDamage, hideCostFor, isRestricted, keywordAmount, matchesFilter, matchesTypeWord, moveToZone, nonHandDiscount, pay, playersAt, recycleCandidates, sacrificeCandidates, sacrificedDiscount, scriptFor, soleOccupant, testStaticCondition, totalCost, unitsAt, unitsOf, victoryTarget, zoneList,
} from './state.ts';
import type {
  Action, CardDef, ChainItem, Cost, DelayedEffect, Domain, GameObject, GameState, Keyword,
  Location, ObjectId, Phase, PlayerId, SpendPurpose, TurnEffect,
} from './types.ts';
import { sameLocation, DECLINE } from './types.ts';

// -- setup -------------------------------------------------------------------

export interface DeckList {
  name: string;
  legend: string;
  chosenChampion: string;
  battlefield: string;
  /** Main deck card ids, 40 or more, including the Chosen Champion's copy. */
  main: string[];
  /** Exactly 12 rune card ids. */
  runes: string[];
}

export function createGame(
  rules: Rules, decks: [DeckList, DeckList], seed: number,
  config = DEFAULT_CONFIG,
): GameState {
  const state: GameState = {
    turnEffects: [], extraTurns: [],
    pendingTriggers: [],
    delayed: [],
    pendingChoice: null, choiceLog: [], choiceCursor: 0,
    triggerChoiceLog: [], triggerChoiceCursor: 0,
    config, seed, turn: 1, turnPlayer: 0, phase: 'awaken',
    players: [makePlayer(0, decks[0].name), makePlayer(1, decks[1].name)],
    objects: {}, nextObjectId: 1,
    zones: {
      mainDeck: [[], []], runeDeck: [[], []], hand: [[], []], trash: [[], []],
      banishment: [[], []], championZone: [[], []], legendZone: [[], []],
    },
    battlefields: [],
    chain: [], nextChainId: 1,
    showdownState: 'neutral', openState: 'open', combat: null,
    priority: null, focus: null, passes: 0,
    pendingAssignment: null, assignments: [],
    waiting: { kind: 'none' }, winner: null, events: [], version: 0,
  };

  // 110-114. Legend to the Legend Zone, Chosen Champion to the Champion Zone,
  // battlefields to the Battlefield Zone, decks shuffled into place.
  for (const player of [0, 1] as PlayerId[]) {
    const deck = decks[player];

    spawn(state, deck.legend, player, { kind: 'legendZone', player });
    spawn(state, deck.chosenChampion, player, { kind: 'championZone', player });

    // 103.2.a.1. The Chosen Champion's copy is one of the Main Deck's cards
    // (103.2), and it "will be placed in the Champion Zone at the start of the
    // game" -- so it leaves the deck to stand there. Spawning it in the zone
    // *and* leaving it in the deck dealt a second copy of a card the player
    // owns one of, which is both an illegal board and a real advantage.
    //
    // Exactly one copy is taken out, never every copy: 103.2.b.1 explicitly
    // allows up to two more of the same card in the deck alongside the one in
    // the zone ("a deck could include Volibear, Furious as its Chosen Champion
    // and still include 2 more copies in their Main Deck").
    const inDeck = [...deck.main];
    const spare = inDeck.indexOf(deck.chosenChampion);
    if (spare >= 0) inDeck.splice(spare, 1);

    for (const id of inDeck) spawn(state, id, player, { kind: 'mainDeck', player });
    for (const id of deck.runes) spawn(state, id, player, { kind: 'runeDeck', player });

    shuffle(state, zoneList(state, 'mainDeck', player));
    shuffle(state, zoneList(state, 'runeDeck', player));

    const bf = spawn(state, deck.battlefield, player, { kind: 'board' });
    bf.location = battlefieldLocation(state.battlefields.length);
    state.battlefields.push({
      index: state.battlefields.length, object: bf.id, controller: null,
      contestedBy: null, scoredThisTurn: [], conqueredThisTurn: [], showdownStaged: false, combatStaged: false,
    });
  }

  // 116. Each player draws 4, then mulligans in turn order (117).
  for (const player of [0, 1] as PlayerId[]) {
    ops.draw(state, rules, player, config.startingHand);
  }
  state.waiting = { kind: 'mulligan', player: 0 };
  return state;
}

const DEFAULT_CONFIG = {
  victoryScore: 8, battlefieldCount: 2, extraRuneForSecondPlayer: true,
  startingHand: 4, mulliganLimit: 2, runesPerChannel: 2, maxCleanupIterations: 64,
};

const makePlayer = (id: PlayerId, name: string) => ({
  id, name, points: 0, pool: emptyPool(), xp: 0,
  cardsPlayedThisTurn: 0, chosenEnemyThisTurn: 0, abilitiesUsedThisTurn: [],
  turnsTaken: 0, hasConceded: false,
  spentThisTurn: { energy: 0, power: 0 }, excessDamageThisTurn: 0,
  unitsDiedThisTurn: 0, unitsDiedInBeginningPhase: 0,
  playedThisTurn: [], xpGainedThisTurn: 0,
  pointsHeldThisTurn: 0,
});

function spawn(
  state: GameState, cardId: string, owner: PlayerId, zone: GameObject['zone'],
): GameObject {
  const id = state.nextObjectId++;
  const o: GameObject = {
    id, card: cardId, owner, controller: owner, zone, location: null,
    exhausted: false, damage: 0, statuses: [], mods: [],
    attachedTo: null, attachments: [], borrowedControl: null,
    enteredTurn: 0, movesThisTurn: 0, conqueredThisTurn: false,
    usedThisTurn: [], playedFrom: null,
  };
  state.objects[id] = o;
  if (zone.kind !== 'board' && zone.kind !== 'chain' && zone.kind !== 'facedown') {
    zoneList(state, zone.kind, zone.player).push(id);
  }
  return o;
}

// -- the loop ----------------------------------------------------------------

/** Runs the game forward until a player has to decide something. */
export function advance(state: GameState, rules: Rules): void {
  let guard = 0;
  while (state.waiting.kind === 'none' && state.winner === null) {
    if (guard++ > 4000) {
      state.events.push({ t: 'gameOver', winner: null, reason: 'engine failed to settle' });
      state.winner = null;
      state.waiting = { kind: 'gameOver' };
      return;
    }

    // 359.3. A trigger picks its targets as it goes on the chain, and those
    // are the controller's to pick -- so a settle can stop to ask. It cannot
    // suspend part-way any more than the resolver can, so one turn of this
    // loop is thrown away and replayed the same way: snapshot, run, and on a
    // question roll back to the top of the iteration and record it.
    //
    // The whole iteration is the unit, because a trigger goes on the chain
    // from four places in it -- the cleanup (conquer), the Might sweep, the
    // drain, and the phase and combat steps (hold and score). A resolution
    // that stops to ask is *not* seen here: `resolveItem` keeps its own
    // snapshot and sets `waiting` rather than throwing past this.
    //
    // The snapshot is skipped unless something on the board could actually
    // ask, because cloning the state every settle would cost far more than
    // this feature is worth.
    if (!triggersMayAsk(state, rules)) {
      if (settleOnce(state, rules)) return;
      continue;
    }
    const beforeStep = structuredClone(state);
    state.triggerChoiceCursor = 0;
    let done = false;
    askArmed = true;
    try {
      done = settleOnce(state, rules);
    } catch (thrown) {
      if (!(thrown instanceof NeedsChoice)) throw thrown;
      Object.assign(state, beforeStep);
      state.pendingChoice = thrown.choice;
      state.waiting = { kind: 'choose', player: thrown.choice.player };
      return;
    } finally {
      askArmed = false;
    }
    state.triggerChoiceLog = [];
    state.triggerChoiceCursor = 0;
    if (done) return;
  }
}

/**
 * One turn of the settle loop. Returns whether `advance` should stop.
 *
 * Split out so the whole of it can be rolled back and replayed as one piece
 * when a trigger stops to ask which target it takes.
 */
function settleOnce(state: GameState, rules: Rules): boolean {
  settleTriggers(state, rules);
  if (state.winner !== null || state.waiting.kind !== 'none') return true;

  if (state.chain.length > 0) { stepChain(state, rules); return false; }
  if (state.combat) { stepCombat(state, rules); return false; }
  stepPhase(state, rules);
  return false;
}

/**
 * Everything in a settle that can put a trigger on the chain, as one unit so
 * it can be rolled back and replayed when a trigger stops to ask.
 */
function settleTriggers(state: GameState, rules: Rules): void {
  runCleanup(state, rules);
  if (state.winner !== null || state.waiting.kind !== 'none') return;

  // 187.4. "You control it until I leave the board" ends the moment the
  // taker leaves, which can be any settle, not only end of turn.
  ops.returnBorrowed(state, false);

  // 818. "... for as long as this is attached to it." A copy held in place by
  // an Equipment ends when that Equipment comes off, which can happen at any
  // settle -- the Equipment can be killed as easily as detached.
  for (const o of Object.values(state.objects)) {
    const held = o?.copiedWhileAttached;
    if (!o || !held) continue;
    const by = state.objects[held.by];
    const stillOn = by && by.zone.kind === 'board'
      && by.attachedTo === o.id;
    if (stillOn) continue;
    o.card = held.was;
    delete o.copiedWhileAttached;
    state.version++;
  }

  // 143. Might thresholds are state-based rather than event-driven: nothing
  // announces "my Might went up", so the crossing is noticed here.
  checkMightThresholds(state, rules);

  // 371. Offer everything ops recorded to the triggered abilities watching
  // for it, before the machine moves on.
  drainTriggers(state, rules);
}

// -- phases ------------------------------------------------------------------

function stepPhase(state: GameState, rules: Rules): void {
  const player = state.turnPlayer;

  switch (state.phase) {
    case 'awaken': {
      state.events.push({ t: 'phase', phase: 'awaken', player, turn: state.turn });
      // 315.1.b. Ready everything this player controls.
      for (const o of boardObjects(state)) {
        if (o.controller === player) ops.ready(state, o);
      }
      state.phase = 'beginning';
      break;
    }

    case 'beginning': {
      state.events.push({ t: 'phase', phase: 'beginning', player, turn: state.turn });
      // 816.1.b. Temporary is shorthand for "at the start of this permanent's
      // controller's Beginning Phase, before scoring, kill this". Before the
      // hold sweep below, which is what "before scoring" means.
      for (const o of boardObjects(state)) {
        if (o.controller !== player) continue;
        if (!hasKeywordNow(state, rules, o, 'temporary')) continue;
        if (suppressedAt(state, rules, o, 'temporary')) continue;
        // Killed by the keyword itself, not by another card.
        ops.kill(state, rules, o, null);
      }
      fireTriggers(state, rules, { on: 'startOfPhase', phase: 'beginning' }, player);
      runDelayed(state, rules, player, 'startOfPhase', 'beginning');
      // 315.2.b.2. Hold every battlefield this player controls.
      for (const bf of state.battlefields) {
        if (bf.controller === player
          && !isRestricted(state, rules, 'score', player,
            { at: battlefieldLocation(bf.index) })) {
          if (replaceScore(state, rules, player)) continue;
          if (ops.score(state, rules, player, bf.index, 'hold')) {
            // 471. Counted apart from conquering: some cards scale off points
            // scored specifically by holding.
            state.players[player].pointsHeldThisTurn++;
            fireTriggersAt(state, rules, { on: 'hold' }, player, bf.index);
            // 363.3. "When they hold, return it to their hand" -- armed by a
            // card that may be long gone, so it lives on the state rather than
            // on the object.
            runDelayed(state, rules, player, 'hold');
            // 470. "When you conquer or hold" watches either, so it is raised
            // alongside both rather than being a third thing to remember.
            fireTriggersAt(state, rules, { on: 'score' }, player, bf.index);
            state.pendingTriggers.push({ on: 'opponentScores', player });
          }
        }
      }
      state.phase = 'channel';
      break;
    }

    case 'channel': {
      state.events.push({ t: 'phase', phase: 'channel', player, turn: state.turn });
      // 485.7. The player going second channels one extra rune on turn one.
      const extra = state.config.extraRuneForSecondPlayer
        && player === 1 && state.players[player].turnsTaken === 0 ? 1 : 0;
      // 485. A permanent can cap what this phase channels -- for everyone, not
      // just its controller. Only the phase is capped; an effect that says
      // "channel 2 runes" is a different thing and is untouched.
      let runes = state.config.runesPerChannel + extra;
      for (const o of boardObjects(state)) {
        for (const e of activePassives(state, rules, o)) {
          if (e.p === 'channelCap') runes = Math.min(runes, e.runes);
        }
      }
      ops.channel(state, rules, player, runes);
      state.phase = 'draw';
      break;
    }

    case 'draw': {
      state.events.push({ t: 'phase', phase: 'draw', player, turn: state.turn });
      // 316.4. "Skip your Draw Phase." The phase still happens -- anything
      // watching for it still fires -- it just draws nothing.
      const skipped = boardObjects(state).some((o) =>
        activePassives(state, rules, o).some((e) => e.p === 'skipDraw'
          && (e.who === 'you' ? o.controller === player : o.controller !== player)));
      if (!skipped) ops.draw(state, rules, player, 1);
      state.phase = 'main';
      // 316.3. Every player's pool empties as the Main Phase starts.
      ops.emptyPools(state);
      fireTriggers(state, rules, { on: 'startOfPhase', phase: 'main' }, player);
      runDelayed(state, rules, player, 'startOfPhase', 'main');
      break;
    }

    case 'main': {
      // 316.5.b. Neutral Open: only the turn player may act.
      state.priority = player;
      state.waiting = { kind: 'priority', player };
      state.events.push({ t: 'priority', player });
      break;
    }

    case 'ending': {
      state.events.push({ t: 'phase', phase: 'ending', player, turn: state.turn });
      fireTriggers(state, rules, { on: 'endOfTurn' }, player);
      runDelayed(state, rules, player, 'endOfTurn');
      // 317.2. The Expiration Step: heal, expire, empty pools.
      ops.healAll(state, rules);
      ops.expireMods(state, 'turn');
      ops.returnBorrowed(state, true);
      ops.emptyPools(state);
      endTurn(state);
      break;
    }
  }
}

function endTurn(state: GameState): void {
  state.events.push({ t: 'turnEnd', player: state.turnPlayer });
  // 363.2. "This turn" effects expire here rather than each carrying its own
  // expiry, so none can outlive the turn by being forgotten.
  state.turnEffects = [];
  state.players[state.turnPlayer].turnsTaken++;
  for (const p of state.players) {
    p.cardsPlayedThisTurn = 0;
    p.chosenEnemyThisTurn = 0;
    p.abilitiesUsedThisTurn = [];
    p.spentThisTurn = { energy: 0, power: 0 };
    p.excessDamageThisTurn = 0;
    p.unitsDiedThisTurn = 0;
    p.unitsDiedInBeginningPhase = 0;
    p.playedThisTurn = [];
    p.xpGainedThisTurn = 0;
    p.pointsHeldThisTurn = 0;
  }
  for (const bf of state.battlefields) {
    bf.scoredThisTurn = [];
    bf.conqueredThisTurn = [];
  }

  // 314. An extra turn goes to whoever was granted it, instead of passing.
  const extra = state.extraTurns.shift();
  state.turnPlayer = extra ?? OPPONENT(state.turnPlayer);
  state.turn++;
  state.phase = 'awaken';
  state.priority = null;
  state.focus = null;
}

// -- cleanup (318) -----------------------------------------------------------

/**
 * The cleanup steps, repeated until the board stops changing (322). This is
 * where most of Riftbound's positional logic actually lives: control of
 * battlefields, contested status, and the staging of showdowns and combats are
 * all decided here rather than by the actions that provoked them.
 */
function runCleanup(state: GameState, rules: Rules): void {
  for (let pass = 0; pass < state.config.maxCleanupIterations; pass++) {
    const before = state.version;

    // 323.1. Victory check.
    if (checkVictory(state, rules)) return;

    // 323.2. Attacker/defender designations follow their controller.
    if (state.combat) syncDesignations(state, rules);

    // 323.3. Deathknell triggers, then lethal-damaged units die.
    const dying = boardObjects(state).filter((o) => hasLethalDamage(state, rules, o));

    // 704. A replacement runs in place of the death, so it is settled before
    // the Deathknell triggers -- a unit that never dies never knells.
    const actuallyDying = dying.filter((o) => !replaceDeath(state, rules, o));

    for (const o of actuallyDying) {
      fireSelfTriggers(state, rules, o, { on: 'dies', self: true });
    }
    for (const o of actuallyDying) {
      if (state.objects[o.id]) ops.kill(state, rules, o, null);
    }

    // 323.4. Lose control of a battlefield with none of your units on it.
    if (state.openState === 'open' && !state.combat) {
      for (const bf of state.battlefields) {
        if (bf.controller === null) continue;
        const mine = unitsOf(state, rules.index, bf.controller, battlefieldLocation(bf.index));
        if (mine.length === 0) {
          state.events.push({
            t: 'controlChanged', battlefield: bf.index, from: bf.controller, to: null,
          });
          bf.controller = null;
          state.version++;
        }
      }
    }

    // 719.3. "A Top-Most Card and all cards Attached to it are at the same
    // location." `ops.move` and `ops.recallToBase` carry attachments as they
    // go, which is what 719.3.a describes; this is the corrective for every
    // other way a host can end up somewhere new -- an effect that writes a
    // location, a token created onto the board, a borrowed permanent handed
    // back. Positional truth is settled in the cleanup loop for the same
    // reason control and contested status are.
    for (const o of boardObjects(state)) {
      if (o.attachedTo === null) continue;
      const host = state.objects[o.attachedTo];
      if (!host || !host.location || sameLocation(o.location, host.location)) continue;
      o.location = host.location;
      state.version++;
    }

    // 323.5. Recall stray non-unit gear and anything sitting in a foreign base.
    for (const o of boardObjects(state)) {
      const def = rules.index.get(o.card);
      if (def.type === 'battlefield') continue;
      const loc = o.location;
      if (!loc) continue;
      const strayGear = def.type === 'gear' && o.attachedTo === null
        && loc.kind === 'battlefield';
      const foreignBase = loc.kind === 'base' && loc.player !== o.controller;
      if (strayGear || foreignBase) ops.recallToBase(state, o);
    }

    // 323.6-8. Stage showdowns and combats, and clear stale contested status.
    for (const bf of state.battlefields) {
      const here = playersAt(state, rules, bf.index);

      if (bf.contestedBy !== null) {
        bf.showdownStaged = true;
        bf.combatStaged = here.length >= 2;
        const stillThere = here.includes(bf.contestedBy);
        const ongoing = state.combat?.battlefield === bf.index;
        if (!stillThere && !ongoing) {
          bf.contestedBy = null;
          bf.showdownStaged = false;
          bf.combatStaged = false;
          state.version++;
        }
      } else {
        bf.showdownStaged = false;
        bf.combatStaged = false;
      }

      // 323.11.a. A unit sitting at a battlefield its controller does not
      // control contests it.
      if (bf.contestedBy === null) {
        for (const p of here) {
          if (bf.controller !== p) {
            bf.contestedBy = p;
            state.events.push({ t: 'contest', battlefield: bf.index, by: p });
            state.version++;
            break;
          }
        }
      }

      // 190.4.a. Outside combat, sole occupancy establishes control.
      if (!state.combat && state.openState === 'open') {
        const sole = soleOccupant(state, rules, bf.index);
        if (sole !== null && bf.controller !== sole && bf.contestedBy === null) {
          establishControl(state, rules, bf.index, sole);
        }
      }
    }

    // 323.9-10. Open a staged showdown or combat.
    if (state.showdownState === 'neutral' && state.openState === 'open' && !state.combat) {
      const staged = state.battlefields.find((b) => b.combatStaged)
        ?? state.battlefields.find((b) => b.showdownStaged);
      if (staged && staged.contestedBy !== null) {
        beginShowdown(state, rules, staged.index, staged.combatStaged);
        return;
      }
    }

    if (state.version === before) return;
  }
}

function checkVictory(state: GameState, rules: Rules): boolean {
  const [a, b] = state.players;
  // 471. Battlefields can raise the bar while they are in play.
  const target = victoryTarget(state, rules);
  for (const p of [a, b]) {
    const rival = state.players[OPPONENT(p.id)];
    if (p.points >= target && p.points > rival.points) {
      state.winner = p.id;
      state.waiting = { kind: 'gameOver' };
      state.events.push({ t: 'gameOver', winner: p.id, reason: 'victory score' });
      return true;
    }
  }
  return false;
}

function syncDesignations(state: GameState, rules: Rules): void {
  const combat = state.combat;
  if (!combat) return;
  const loc = battlefieldLocation(combat.battlefield);
  for (const o of boardObjects(state)) {
    if (!isUnitType(rules.index.get(o.card).type)) continue;
    const here = sameLocation(o.location, loc);
    const want = !here ? null : o.controller === combat.attacker ? 'attacking' : 'defending';
    const had = o.statuses.find((s) => s === 'attacking' || s === 'defending') ?? null;
    if (had === want) continue;
    o.statuses = o.statuses.filter((s) => s !== 'attacking' && s !== 'defending');
    if (want) o.statuses.push(want);
    state.version++;
  }
}

/**
 * Keeps the battlefield *card* owned by whoever controls the battlefield.
 *
 * `bf.controller` is the game's answer to who holds the place; the object is
 * what abilities are read off. Letting the two disagree is what made a
 * battlefield's own ability fire for the wrong player.
 */
function syncBattlefieldControl(state: GameState, bf: { object: ObjectId; controller: PlayerId | null }): void {
  const card = state.objects[bf.object];
  if (card && bf.controller !== null) card.controller = bf.controller;
}

/** 466.5.d / 469.1. Taking control scores a Conquer if not already scored. */
function establishControl(
  state: GameState, rules: Rules, index: number, player: PlayerId,
): void {
  const bf = battlefield(state, index);
  const from = bf.controller;
  bf.controller = player;
  bf.contestedBy = null;
  // The card lying at the battlefield is controlled by whoever controls the
  // battlefield. It was left at player 0 for the whole game, so "when you hold
  // here" was read as player 0's ability whoever actually held the place: the
  // player who took it never got its trigger, and the player who did not kept
  // getting it.
  syncBattlefieldControl(state, bf);
  state.events.push({ t: 'controlChanged', battlefield: index, from, to: player });
  state.version++;

  if (!isRestricted(state, rules, 'score', player, { at: battlefieldLocation(index) })
    && !replaceScore(state, rules, player)
    && ops.score(state, rules, player, index, 'conquer')) {
    // 471. Recorded separately from scoring: "a battlefield you conquered this
    // turn" is the narrower of the two ways to score here.
    if (!bf.conqueredThisTurn.includes(player)) bf.conqueredThisTurn.push(player);
    // 471. And on each unit that was there for it, which is what an Equipment
    // asking "if I didn't conquer this turn" reads.
    for (const o of unitsOf(state, rules.index, player, battlefieldLocation(index))) {
      o.conqueredThisTurn = true;
    }
    fireTriggersAt(state, rules, { on: 'conquer' }, player, index);
    fireTriggersAt(state, rules, { on: 'score' }, player, index);
    state.pendingTriggers.push({ on: 'opponentScores', player });
  }
}

// -- showdowns and combat ----------------------------------------------------

function beginShowdown(
  state: GameState, rules: Rules, index: number, asCombat: boolean,
): void {
  const bf = battlefield(state, index);
  const attacker = bf.contestedBy;
  if (attacker === null) return;

  state.showdownState = 'showdown';
  state.focus = attacker;
  state.priority = attacker;

  if (asCombat) {
    const others = playersAt(state, rules, index).filter((p) => p !== attacker);
    const defender = others[0] ?? OPPONENT(attacker);
    state.combat = { battlefield: index, attacker, defender, step: 'showdown', isCombat: true };
    state.events.push({ t: 'combatBegin', battlefield: index, attacker, defender });
    // 464. The defending side sees its own battlefield come under attack.
    state.pendingTriggers.push({ on: 'enemyAttacksYourBattlefield', player: defender });
    syncDesignations(state, rules);
    // 464.2.e. Attack and defend triggers go on the Combat Chain.
    for (const u of unitsAt(state, rules.index, battlefieldLocation(index))) {
      const which: Trigger = u.controller === attacker ? { on: 'attacks' } : { on: 'defends' };
      fireSelfTriggers(state, rules, u, which);
      fireSelfTriggers(state, rules, u, { on: 'attacksOrDefends' });
    }
  } else {
    state.combat = {
      battlefield: index, attacker, defender: OPPONENT(attacker),
      step: 'showdown', isCombat: false,
    };
  }

  state.events.push({ t: 'showdownBegin', battlefield: index, focus: attacker });
  state.passes = 0;
  state.waiting = { kind: 'priority', player: attacker };
}

function stepCombat(state: GameState, rules: Rules): void {
  const combat = state.combat;
  if (!combat) return;

  switch (combat.step) {
    case 'showdown':
      // The showdown is over once both players have passed with an empty chain.
      if (state.chain.length > 0) return;
      if (state.passes < 2) {
        state.waiting = { kind: 'priority', player: state.focus ?? combat.attacker };
        return;
      }
      state.events.push({ t: 'showdownEnd', battlefield: combat.battlefield });
      if (combat.isCombat) {
        state.pendingTriggers.push({ on: 'startOfCombat', player: combat.attacker });
        state.pendingTriggers.push({ on: 'startOfCombat', player: combat.defender });
      }
      combat.step = combat.isCombat ? 'damage' : 'resolution';
      state.showdownState = 'neutral';
      break;

    case 'damage':
      stepCombatDamage(state, rules, combat.battlefield);
      break;

    case 'resolution':
      resolveCombat(state, rules);
      break;
  }
}

/**
 * 465.2. Each side assigns its summed Might among the other's units, lethal
 * first, then all of it is dealt at once. Stunned units contribute nothing
 * (423.1.b).
 */
function stepCombatDamage(state: GameState, rules: Rules, index: number): void {
  const combat = state.combat;
  if (!combat) return;
  const loc = battlefieldLocation(index);

  const attackers = unitsOf(state, rules.index, combat.attacker, loc);
  const defenders = unitsOf(state, rules.index, combat.defender, loc);

  if (attackers.length === 0 || defenders.length === 0) {
    combat.step = 'resolution';
    return;
  }

  if (!state.pendingAssignment) {
    const total = sumMight(state, rules, attackers);
    state.pendingAssignment = {
      player: combat.attacker, total, targets: defenders.map((d) => d.id),
    };
    state.assignments = [];
  }

  const pending = state.pendingAssignment;
  state.waiting = { kind: 'assignDamage', player: pending.player };
}

/**
 * 465. Whether this unit contributes its Might to combat.
 *
 * A unit can be silenced by its own passive or by another permanent's aura, so
 * both the unit and the rest of the board are consulted.
 */
function dealsCombatDamage(state: GameState, rules: Rules, u: GameObject): boolean {
  if (activePassives(state, rules, u).some((e) => e.p === 'noCombatDamage' && !e.filter)) {
    return false;
  }
  for (const source of boardObjects(state)) {
    for (const e of activePassives(state, rules, source)) {
      if (e.p !== 'noCombatDamage' || !e.filter) continue;
      if (matchesFilter(state, rules, u, e.filter, source)) return false;
    }
  }
  return true;
}

const sumMight = (state: GameState, rules: Rules, units: GameObject[]): number =>
  units.reduce((a, u) => a
    + (u.statuses.includes('stunned') || !dealsCombatDamage(state, rules, u)
      ? 0 : effectiveMight(state, rules, u)), 0);

/**
 * Turns an ordering of enemy units into a damage assignment. Lethal damage must
 * be completed on one unit before moving to the next (465.2.c.3) and no unit may
 * be given more than lethal while others remain (465.2.c.4).
 */
export function assignmentFor(
  state: GameState, rules: Rules, order: ObjectId[], total: number,
): { object: ObjectId; amount: number }[] {
  const out: { object: ObjectId; amount: number }[] = [];
  let remaining = total;
  for (let i = 0; i < order.length && remaining > 0; i++) {
    const target = state.objects[order[i]!];
    if (!target) continue;
    const lethal = Math.max(1, effectiveMight(state, rules, target) - target.damage);
    const last = i === order.length - 1;
    const amount = last ? remaining : Math.min(remaining, lethal);
    out.push({ object: target.id, amount });
    remaining -= amount;
  }
  return out;
}

/**
 * 815 / 826. Tank must be assigned damage before units without it; Backline
 * after. Anything else may be ordered freely.
 */
export function damageOrderRank(state: GameState, rules: Rules, unit: GameObject): number {
  if (hasKeywordNow(state, rules, unit, 'tank')
    && !ignoredInCombatAt(state, rules, unit.location, 'tank')) return 0;
  if (hasKeywordNow(state, rules, unit, 'backline')
    && !ignoredInCombatAt(state, rules, unit.location, 'backline')) return 2;
  return 1;
}

/**
 * 815. Whether something standing at `at` switches off a keyword's hold on the
 * damage order there. The unit keeps the keyword; it just stops constraining.
 */
function ignoredInCombatAt(
  state: GameState, rules: Rules, at: Location | null, keyword: Keyword,
): boolean {
  if (!at) return false;
  return boardObjects(state).some((o) =>
    sameLocation(o.location, at)
    && activePassives(state, rules, o).some(
      (e) => e.p === 'ignoreKeywordInCombat' && e.keyword === keyword));
}

export function isLegalDamageOrder(
  state: GameState, rules: Rules, order: ObjectId[],
): boolean {
  let rank = -1;
  for (const id of order) {
    const o = state.objects[id];
    if (!o) return false;
    const r = damageOrderRank(state, rules, o);
    if (r < rank) return false;
    rank = Math.max(rank, r);
  }
  return true;
}

export function defaultDamageOrder(
  state: GameState, rules: Rules, targets: ObjectId[],
): ObjectId[] {
  return [...targets].sort((a, b) => {
    const oa = state.objects[a]!, ob = state.objects[b]!;
    const ra = damageOrderRank(state, rules, oa), rb = damageOrderRank(state, rules, ob);
    if (ra !== rb) return ra - rb;
    // Within a rank, kill the biggest threat first.
    return effectiveMight(state, rules, ob) - effectiveMight(state, rules, oa);
  });
}

/** 466. Combat cleanup, result, control, end. */
function resolveCombat(state: GameState, rules: Rules): void {
  const combat = state.combat;
  if (!combat) return;
  const index = combat.battlefield;
  const loc = battlefieldLocation(index);

  // 469. Both sides see the combat end.
  // 463. Where it happened, so "a combat that I was in" can tell whether it
  // was this one.
  const at = battlefieldLocation(combat.battlefield);
  state.pendingTriggers.push({ on: 'endOfCombat', player: combat.attacker, from: at });
  state.pendingTriggers.push({ on: 'endOfCombat', player: combat.defender, from: at });

  if (combat.isCombat) {
    // 466.1.a.1-2. Heal everything, then recall attackers if defenders survived.
    ops.healAll(state, rules);
    const defenders = unitsOf(state, rules.index, combat.defender, loc);
    if (defenders.length > 0) {
      // 466. A tie -- both sides still standing -- can be replaced: "recall ALL
      // units instead", which sends the defenders home too.
      const both = boardObjects(state).some((o) =>
        activePassives(state, rules, o).some((e) =>
          e.p === 'replaceTie'
          && (!e.asAttacker || o.controller === combat.attacker)));
      const going = both
        ? [...unitsOf(state, rules.index, combat.attacker, loc),
          ...unitsOf(state, rules.index, combat.defender, loc)]
        : unitsOf(state, rules.index, combat.attacker, loc);
      for (const a of going) ops.recallToBase(state, a);
    }
  }

  const remaining = playersAt(state, rules, index);
  const winner = remaining.length === 1 ? remaining[0]! : null;
  state.events.push({ t: 'combatResult', battlefield: index, winner });

  // 469. "When it wins a combat this turn, gain 2 XP." A combat announces its
  // players, not its units, so a promise made about one unit is armed on that
  // unit and spent here -- by the first combat its side wins with it present.
  if (winner !== null) {
    const armed = state.turnEffects.filter((e) => e.k === 'winsCombat');
    for (const e of armed) {
      if (e.k !== 'winsCombat') continue;
      const unit = state.objects[e.object];
      if (!unit || unit.controller !== winner) continue;
      if (!sameLocation(unit.location, loc)) continue;
      state.turnEffects = state.turnEffects.filter((x) => x !== e);
      const from = state.objects[e.source];
      if (!from) continue;
      runEffects({
        state, rules, source: from, controller: e.player,
        chosen: [unit.id], lastDealKilled: false,
        revealed: [], didIt: false, paidAdditionalCost: false,
      }, e.effects);
    }
  }

  const bf = battlefield(state, index);
  if (winner !== null) {
    if (bf.controller !== winner) establishControl(state, rules, index, winner);
    else bf.contestedBy = null;
  } else if (remaining.length === 0) {
    const from = bf.controller;
    if (from !== null) {
      state.events.push({ t: 'controlChanged', battlefield: index, from, to: null });
    }
    bf.controller = null;
    bf.contestedBy = null;
  }

  bf.showdownStaged = false;
  bf.combatStaged = false;

  // 466.7.a. Designations and "this combat" effects end with the combat.
  for (const o of boardObjects(state)) {
    o.statuses = o.statuses.filter((s) => s !== 'attacking' && s !== 'defending');
  }
  ops.expireMods(state, 'combat');

  state.events.push({ t: 'combatEnd', battlefield: index });
  state.combat = null;
  state.showdownState = 'neutral';
  state.focus = null;
  state.pendingAssignment = null;
  state.assignments = [];
  state.passes = 0;
  state.version++;
}

// -- the chain (337-340) -----------------------------------------------------

function stepChain(state: GameState, rules: Rules): void {
  // Step 1: Finalize the oldest pending item.
  const pending = state.chain.find((i) => !i.finalized);
  if (pending) {
    finalize(state, rules, pending);
    return;
  }

  // Steps 2 and 3: the controller of the top item gets priority; when everyone
  // has passed in sequence, resolve.
  if (state.passes >= 2) {
    resolveTop(state, rules);
    return;
  }

  const top = state.chain[state.chain.length - 1];
  const holder = state.priority ?? (top ? OPPONENT(top.controller) : state.turnPlayer);
  state.priority = holder;
  state.waiting = { kind: 'priority', player: holder };
}

function finalize(state: GameState, rules: Rules, item: ChainItem): void {
  item.finalized = true;
  state.version++;

  // 337.2 / 429.2. A permanent, or an ability that only adds resources,
  // resolves the moment it finalizes rather than waiting for priority.
  const immediate = item.kind === 'permanent' || item.addsResources === true;
  if (immediate) resolveItem(state, rules, item);
  else state.passes = 0;
}

function resolveTop(state: GameState, rules: Rules): void {
  const item = state.chain[state.chain.length - 1];
  if (!item) return;
  resolveItem(state, rules, item);
}

/**
 * 355.3 / 424. Resolves one chain item, stopping to ask if it has to.
 *
 * The resolver cannot suspend, so a question is answered by throwing the
 * half-finished resolution away and asking; the answer goes on `choiceLog` and
 * the whole item resolves again, reading the log where it asked before.
 * Determinism is what makes that sound -- the rollback restores the seed with
 * everything else, so the replay reveals the same cards and reaches the same
 * question with one more answer in hand.
 *
 * The snapshot is only taken when the item's effects contain something that
 * can ask. Cloning the state on every resolution would cost more than the
 * feature is worth, and most resolutions have nothing to ask about.
 */
function resolveItem(state: GameState, rules: Rules, item: ChainItem): void {
  const source = state.objects[item.source];
  const effects = !source ? []
    : item.kind === 'spell' ? spellEffects(rules, source.card)
    : item.kind === 'permanent' ? []
    : (item.effects ?? []);
  if (!mayAsk(effects)) { resolveItemNow(state, rules, item); return; }

  const before = structuredClone(state);
  state.choiceCursor = 0;
  try {
    resolveItemNow(state, rules, item);
  } catch (thrown) {
    if (!(thrown instanceof NeedsChoice)) throw thrown;
    // Back to the instant before the item started, question in hand. Nothing
    // half-done survives, including the events the attempt pushed -- the
    // client must not animate a resolution that is about to happen again.
    Object.assign(state, before);
    state.pendingChoice = thrown.choice;
    state.waiting = { kind: 'choose', player: thrown.choice.player };
    return;
  }
  state.choiceLog = [];
  state.choiceCursor = 0;
  state.pendingChoice = null;
}

/** The instructions that stop and ask someone a question (355.3, 424). */
const ASKING_OPS: ReadonlySet<string> = new Set([
  // 424. A reveal can stop to ask too, when someone is watching for it.
  'pickRevealed', 'takeRevealed', 'chooseMode', 'nameIt', 'defenderKills', 'reveal',
  // 424. "You may recycle one or both of them" asks once per card.
  'recycleRevealed',
]);

/**
 * Whether an instruction tree contains anything that stops to ask (355.3, 424).
 *
 * This gates the snapshot, so a false answer is not a missed optimisation: the
 * resolver throws `NeedsChoice` with no saved state to roll back to, and the
 * throw escapes the engine entirely and takes the match with it.
 *
 * The nesting is therefore walked *structurally* -- every field that holds a
 * list of instructions -- rather than by naming the containers. Naming them is
 * what broke it: the list knew `if`, `repeat` and `forEach`, while the compiler
 * had grown nine more (`payTo.then`, `eachPlayer.then`, `delay.effects`,
 * `armNextTime.effects`, `asPlayer.then`, ...), and Promising Future's "each
 * player looks at the top 5 and chooses one" -- an ask inside `eachPlayer` --
 * crashed on resolution. A structural walk cannot fall behind the grammar.
 */
export function mayAsk(effects: readonly Instruction[]): boolean {
  for (const i of effects) {
    if (ASKING_OPS.has(i.op)) return true;
    for (const nested of Object.values(i as Record<string, unknown>)) {
      if (!Array.isArray(nested)) continue;
      if (!nested.every((n) => !!n && typeof n === 'object' && typeof n.op === 'string')) continue;
      if (mayAsk(nested as readonly Instruction[])) return true;
    }
  }
  return false;
}

function resolveItemNow(state: GameState, rules: Rules, item: ChainItem): void {
  const i = state.chain.indexOf(item);
  if (i >= 0) state.chain.splice(i, 1);

  const source = state.objects[item.source];
  if (source) {
    state.events.push({ t: 'resolve', item: item.id, object: source.id, card: source.card });

    if (item.kind === 'permanent') {
      enterBoard(state, rules, source, item);
    } else if (item.kind === 'spell') {
      const effects = spellEffects(rules, source.card);
      run(state, rules, source, item, effects);
      // 820. Repeat runs the spell's effect a second time, with the same
      // choices -- the targets were settled once, when it was played.
      if (item.paidRepeat) run(state, rules, source, item, effects);
      // 829.1.b. A spell played from the trash for its Flow cost is banished
      // rather than trashed -- otherwise it could be replayed every turn.
      moveToZone(state, rules, source, item.paidFlow
        ? { kind: 'banishment', player: source.owner }
        : { kind: 'trash', player: source.owner });
    } else {
      run(state, rules, source, item, item.effects ?? []);
    }
  }

  state.passes = 0;
  state.priority = null;
  if (state.chain.length === 0) {
    state.openState = 'open';
    // 340.2.a. Focus passes when a showdown chain empties.
    //
    // Not for an ability that only adds resources. Every rune prints the rule
    // on its own face -- "Abilities that add resources can't be reacted to" --
    // so tapping one never opened a window and cannot be what closes one. It
    // used to hand focus to the opponent all the same, which meant that inside
    // a showdown a player could tap once or twice and then lose priority with
    // the cost half paid. Anything costing three resources was unpayable in a
    // showdown, and the client reported it as "not enough resources" while the
    // runes stood there ready.
    if (state.showdownState === 'showdown' && state.focus !== null
      && !item.fromTrigger && item.addsResources !== true) {
      state.focus = OPPONENT(state.focus);
      state.priority = state.focus;
      state.events.push({ t: 'focus', player: state.focus });
    }
  }
  state.version++;
}

function run(
  state: GameState, rules: Rules, source: GameObject, item: ChainItem,
  effects: readonly Instruction[],
): void {
  if (effects.length === 0) return;
  const ctx: EffectContext = {
    state, rules, source, controller: item.controller,
    chosen: item.targets, lastDealKilled: false,
    revealed: [], didIt: false, paidAdditionalCost: item.paidAdditional,
    // 203.1. What the price consumed, for "the unit you exhausted to pay".
    ...(item.paidWith ? { paidWith: item.paidWith } : {}),
  };
  runEffects(ctx, effects);
}

function spellEffects(rules: Rules, cardId: string): Instruction[] {
  for (const a of abilitiesOf(rules, cardId)) {
    if (a.kind === 'spell') return a.effects;
  }
  return [];
}

/** 351.1. A permanent becomes a game object on the board when it resolves. */
function enterBoard(
  state: GameState, rules: Rules, source: GameObject, item: ChainItem,
): void {
  const def = rules.index.get(source.card);
  moveToZone(state, rules, source, { kind: 'board' });
  source.controller = item.controller;
  source.location = item.destination ?? baseLocation(item.controller);

  // 143.4. Units enter exhausted; gear enters ready (149.1). Accelerate and
  // "I enter ready" passives override that.
  let exhausted = isUnitType(def.type);
  if (item.paidAccelerate) exhausted = false;
  // Through activePassives: "If an opponent controls a battlefield, I enter
  // ready" is a gated passive, and reading the raw effect list would see the
  // conditional wrapper rather than the entersReady inside it.
  for (const e of activePassives(state, rules, source)) {
    // 356.2. "If you do, I enter ready": only when the optional additional
    // cost was actually taken, which only this play knows.
    if (e.p === 'entersReady' && (!e.ifPaidAdditional || item.paidAdditional)) exhausted = false;
    if (e.p === 'entersExhausted') exhausted = true;
  }

  // "Units you play this turn enter ready" is not spent by the first one.
  if (isUnitType(def.type) && exhausted
    && state.turnEffects.some(
      (e) => e.k === 'unitsEnterReady' && e.player === item.controller)) {
    exhausted = false;
  }

  // "The next unit you play this turn enters ready" is spent by the first unit
  // that uses it (363.2).
  if (isUnitType(def.type) && exhausted) {
    const i = state.turnEffects.findIndex(
      (e) => e.k === 'nextUnitEntersReady' && e.player === item.controller,
    );
    if (i >= 0) {
      state.turnEffects.splice(i, 1);
      exhausted = false;
    }
  }
  source.exhausted = exhausted;

  // 363.2. "When a friendly unit is played this turn, buff it." Every unit
  // that arrives, so this is not spent -- unlike the "next time" form below.
  if (isUnitType(def.type)) {
    for (const e of [...state.turnEffects]) {
      if (e.k !== 'onUnitPlayed' || e.player !== item.controller) continue;
      const from = state.objects[e.source];
      if (!from || !matchesFilter(state, rules, source, e.filter, from)) continue;
      runEffects({
        state, rules, source: from, controller: e.player,
        chosen: [source.id], lastDealKilled: false,
        revealed: [], didIt: false, paidAdditionalCost: false,
      }, e.effects);
    }
  }

  // 363.3. "The next time you play a unit this turn, ready it." Spent by the
  // first unit that arrives, and run with the arming card as its source.
  if (isUnitType(def.type)) {
    const i = state.turnEffects.findIndex(
      (e) => e.k === 'nextUnitPlayed' && e.player === item.controller,
    );
    const armed = i >= 0 ? state.turnEffects[i] : undefined;
    if (armed?.k === 'nextUnitPlayed') {
      state.turnEffects.splice(i, 1);
      const from = state.objects[armed.source];
      if (from) {
        runEffects({
          state, rules, source: from, controller: armed.player,
          chosen: [source.id], lastDealKilled: false,
          revealed: [], didIt: false, paidAdditionalCost: false,
        }, armed.effects);
      }
    }
  }

  state.events.push({
    t: 'play', player: item.controller, object: source.id,
    card: source.card, to: source.location,
  });
  fireSelfTriggers(state, rules, source, { on: 'played', self: true });
  if (source.statuses.includes('facedown')) {
    fireSelfTriggers(state, rules, source, { on: 'playedFromHidden', self: true });
  }
  // 349. Everyone else sees that a card was played, which is a different
  // trigger from the card's own "when you play me". A spell raises this as it
  // goes on the chain instead, having no board to enter.
  state.pendingTriggers.push({
    on: 'playerPlaysCard', player: item.controller, subject: source.id,
  });
  state.version++;
}

// -- triggers ----------------------------------------------------------------

function triggerMatches(
  a: Trigger, b: Trigger,
  ctx?: { state: GameState; rules: Rules; subject: GameObject | undefined; watcher: GameObject },
): boolean {
  if (a.on !== b.on) return false;
  if (a.on === 'startOfPhase' && b.on === 'startOfPhase') return a.phase === b.phase;

  // "When another non-Recruit unit you control dies" narrows which death
  // counts. Without this every unitDies trigger fired on every death.
  const filter = (a as { filter?: ObjectFilter }).filter;
  if (filter && ctx) {
    if (!ctx.subject) return false;
    if (!matchesFilter(ctx.state, ctx.rules, ctx.subject, filter, ctx.watcher)) return false;
  }
  return true;
}

/**
 * Offers everything that has happened since the last pass to every triggered
 * ability on the board.
 *
 * Twenty-five of the thirty-six declared trigger kinds were compiled into card
 * data while nothing ever raised them, so those cards counted as modelled and
 * sat inert. Anything `ops` announces is matched here.
 */
/**
 * 143. Fires "when my [M] becomes N or more" on the pass where it becomes true.
 *
 * Might is computed rather than stored, so there is no event to hang this on.
 * The crossing is recorded on the object so it fires once per crossing instead
 * of on every settle while the threshold holds.
 */
function checkMightThresholds(state: GameState, rules: Rules): void {
  const board = boardObjects(state);

  for (const o of board) {
    const abilities = abilitiesOn(state, rules, o);
    for (let i = 0; i < abilities.length; i++) {
      const ability = abilities[i]!;
      if (ability.kind !== 'triggered') continue;

      // "When my [M] becomes N or more" -- about the watcher itself.
      if (ability.trigger.on === 'mightReaches') {
        const key = `m${i}`;
        const over = effectiveMight(state, rules, o) >= ability.trigger.amount;
        if (!over) {
          o.usedThisTurn = o.usedThisTurn.filter((k) => k !== key);
          continue;
        }
        if (o.usedThisTurn.includes(key)) continue;
        o.usedThisTurn.push(key);
        pushTrigger(state, rules, o, ability);
        continue;
      }

      // 143.2. "When a unit you control becomes [Mighty]" -- about someone
      // else crossing 5 Might. Keyed per watcher and per unit so it fires once
      // per crossing rather than once per settle.
      if (ability.trigger.on !== 'unitBecomesMighty') continue;
      const filter = ability.trigger.filter ?? { type: 'unit', side: 'friendly' };
      for (const unit of board) {
        if (!isUnitType(rules.index.get(unit.card).type)) continue;
        if (!matchesFilter(state, rules, unit, filter, o)) continue;
        const key = `M${i}:${unit.id}`;
        if (effectiveMight(state, rules, unit) < 5) {
          o.usedThisTurn = o.usedThisTurn.filter((k) => k !== key);
          continue;
        }
        if (o.usedThisTurn.includes(key)) continue;
        o.usedThisTurn.push(key);
        pushTrigger(state, rules, o, ability);
      }
    }
  }
}

function drainTriggers(state: GameState, rules: Rules): void {
  // Bounded: a trigger can announce more events as it resolves, and a pair of
  // cards that trigger each other would otherwise spin forever.
  for (let pass = 0; pass < 20 && state.pendingTriggers.length > 0; pass++) {
    const events = state.pendingTriggers;
    state.pendingTriggers = [];
    for (const event of events) {
      const subject = event.subject !== undefined ? state.objects[event.subject] : undefined;
      for (const watcher of boardObjects(state)) {
        const abilities = abilitiesOn(state, rules, watcher);
        for (let i = 0; i < abilities.length; i++) {
          const ability = abilities[i]!;
          if (ability.kind !== 'triggered') continue;
          if (ability.trigger.on !== event.on) continue;
          // "you ..." triggers belong to the player who acted; "opponent ..."
          // triggers belong to everyone else.
          if (event.on.startsWith('you') && watcher.controller !== event.player) continue;
          if (event.on.startsWith('opponent') && watcher.controller === event.player) continue;
          // 355.9. "When *you* choose me" is about this card's controller doing
          // the choosing. The event itself is player-agnostic, because "when a
          // player chooses a unit here" watches the same thing.
          if (ability.trigger.on === 'chosen' && ability.trigger.by === 'you'
            && watcher.controller !== event.player) continue;
          if (!triggerMatches(ability.trigger, { on: event.on } as Trigger,
            { state, rules, subject, watcher })) continue;
          // 144. "moves to a battlefield other than mine" -- the comparison is
          // between two objects, which an object filter cannot express.
          // 463. "A combat that I was in" is the one at the watcher's own
          // battlefield, which only the event's location can settle.
          if (ability.trigger.on === 'endOfCombat' && ability.trigger.here) {
            if (!watcher.location || !event.from) continue;
            if (!sameLocation(event.from, watcher.location)) continue;
          }
          // "When a unit moves from here" -- the comparison is the watcher's
          // location against where the unit started, which only the event knows.
          if (ability.trigger.on === 'unitMoves' && ability.trigger.fromHere) {
            if (!watcher.location || !event.from) continue;
            if (!sameLocation(event.from, watcher.location)) continue;
          }
          if (ability.trigger.on === 'opponentMoves' && ability.trigger.elsewhere) {
            if (!subject || subject.location?.kind !== 'battlefield') continue;
            if (watcher.location && sameLocation(subject.location, watcher.location)) continue;
          }
          // 371. "The second card you play each turn" fires on that card only.
          if (ability.trigger.on === 'nthCardPlayed'
            && state.players[event.player].cardsPlayedThisTurn !== ability.trigger.nth) {
            continue;
          }
          if (ability.once) {
            const key = `t${i}`;
            if (watcher.usedThisTurn.includes(key)) continue;
            watcher.usedThisTurn.push(key);
          }
          // 375. "When a player plays a spell, they may ..." acts for whoever
          // played, not for whoever holds the battlefield.
          pushTrigger(
            state, rules, watcher, ability,
            ability.byEventPlayer ? event.player : watcher.controller,
          );
        }
      }
    }
  }
}

/** Puts every matching ability of one object onto the chain. */
function fireSelfTriggers(
  state: GameState, rules: Rules, source: GameObject, trigger: Trigger,
  // Whose phase or action it was. Only the `anyPlayer` phase triggers care --
  // for everything else this is the source's own controller.
  actor: PlayerId = source.controller,
): void {
  const abilities = abilitiesOn(state, rules, source);
  for (let i = 0; i < abilities.length; i++) {
    const ability = abilities[i]!;
    if (ability.kind !== 'triggered') continue;
    if (!triggerMatches(ability.trigger, trigger,
      { state, rules, subject: source, watcher: source })) continue;
    // "... their first Beginning Phase": that player's opening turn only.
    if (ability.trigger.on === 'startOfPhase' && ability.trigger.first
      && state.players[actor].turnsTaken > 0) continue;
    // 371. "The first time ... each turn" fires once; keyed by position so two
    // once-per-turn triggers on one card stay independent.
    if (ability.once) {
      const key = `t${i}`;
      if (source.usedThisTurn.includes(key)) continue;
      source.usedThisTurn.push(key);
    }
    pushTrigger(state, rules, source, ability, actor);

    // 383. "Your [Deathknell] effects trigger an additional time." The extra
    // copy is of the whole ability, so it goes on the chain as its own item.
    for (let n = extraTriggersFor(state, rules, source); n > 0; n--) {
      pushTrigger(state, rules, source, ability, actor);
    }
  }
}

/**
 * How many extra times an ability on `source` fires, from doublers on the
 * board. Keyed on a keyword the source actually carries, so "your Deathknell
 * effects" reaches only the abilities Deathknell granted.
 */
function extraTriggersFor(state: GameState, rules: Rules, source: GameObject): number {
  let extra = 0;
  for (const other of boardObjects(state)) {
    if (other.controller !== source.controller) continue;
    for (const e of activePassives(state, rules, other)) {
      if (e.p !== 'extraKeywordTrigger') continue;
      if (hasKeywordNow(state, rules, source, e.keyword)) extra++;
    }
  }
  return extra;
}

/** Scans every board object a player controls for a matching trigger. */
function fireTriggers(
  state: GameState, rules: Rules, trigger: Trigger, player: PlayerId,
): void {
  for (const o of boardObjects(state)) {
    // A phase trigger marked `anyPlayer` belongs to whoever's phase it is, so
    // an uncontrolled battlefield still fires it. Everything else is its
    // controller's.
    const anyPlayer = trigger.on === 'startOfPhase' && matchesAnyPlayerPhase(rules, o, trigger);
    if (!anyPlayer && o.controller !== player) continue;
    fireSelfTriggers(state, rules, o, trigger, player);
  }
}

/** Whether `o` carries a phase trigger that watches both players' phases. */
function matchesAnyPlayerPhase(
  rules: Rules, o: GameObject, trigger: Extract<Trigger, { on: 'startOfPhase' }>,
): boolean {
  return abilitiesOf(rules, o.card).some((a) =>
    a.kind === 'triggered' && a.trigger.on === 'startOfPhase'
    && a.trigger.phase === trigger.phase && a.trigger.anyPlayer === true);
}

/** Score triggers fire only at the battlefield that scored (471.2). */
function fireTriggersAt(
  state: GameState, rules: Rules, trigger: Trigger, player: PlayerId, index: number,
): void {
  const loc = battlefieldLocation(index);

  // 471.2. "Your conquer effects for conquering here trigger an additional
  // time." Counted before anything fires, so a doubler cannot double itself by
  // arriving partway through the sweep.
  let times = 1;
  for (const o of boardObjects(state)) {
    if (o.controller !== player) continue;
    if (!sameLocation(o.location, loc)) continue;
    for (const e of activePassives(state, rules, o)) {
      if (e.p === 'extraScoreTrigger' && e.on === trigger.on) times++;
    }
  }

  // 471. "My hold effects are also conquer effects, and vice versa" makes the
  // two aliases on that card alone, so the swap is decided per watcher.
  const twin = trigger.on === 'conquer' ? 'hold' : trigger.on === 'hold' ? 'conquer' : null;

  for (let n = 0; n < times; n++) {
    for (const o of boardObjects(state)) {
      if (o.controller !== player) continue;
      // 383.4.d.2. A *unit's* hold or conquer ability is about the unit: it
      // fires only when that unit is standing at the battlefield that scored
      // (383.4.d.2.a). Anything else -- a gear, a legend, a battlefield --
      // cannot hold anything itself, so its ability is written about the
      // *player* and fires wherever the permanent happens to be (383.4.d.2.b).
      //
      // Admitting every permanent at a base fired "When I hold, buff all units
      // here" for a unit sitting in its own base that had never been near a
      // battlefield, and buffed the base with it.
      const type = rules.index.get(o.card).type;
      if (isUnitType(type) && !sameLocation(o.location, loc)) continue;
      // 471.2. "Trigger Score abilities *at the Battlefield that Scored*." A
      // battlefield's own "when you conquer/hold here" is about itself, so it
      // fires only when it is the one that scored -- it is not a unit, so the
      // location test above skipped it entirely and every battlefield on the
      // table fired whenever its controller scored anywhere. Holding one
      // battlefield triggered the ability printed on another.
      if (type === 'battlefield' && o.id !== battlefield(state, index).object) continue;
      fireSelfTriggers(state, rules, o, trigger);
      // 471. The alias can be the object's own, or handed to it by something
      // else standing here -- a battlefield that turns holds into conquers for
      // everyone on it.
      const aliased = activePassives(state, rules, o)
        .some((e) => e.p === 'holdAndConquerAlias' && !e.filter)
        || boardObjects(state).some((other) => activePassives(state, rules, other)
          .some((e) => e.p === 'holdAndConquerAlias' && e.filter
            && matchesFilter(state, rules, o, e.filter, other)));
      if (twin && aliased) {
        fireSelfTriggers(state, rules, o, { on: twin });
      }
    }
  }
}

/**
 * 704. Looks for a replacement for `victim`'s death and, if one applies, runs
 * it instead. Returns whether the death was replaced.
 *
 * The dying unit is handed to the instructions as the chosen referent, which
 * is what "instead heal it, exhaust it, and recall it" reads.
 */
function replaceDeath(state: GameState, rules: Rules, victim: GameObject): boolean {
  // 363.3. A one-shot armed earlier this turn takes precedence, and is spent
  // whether or not a standing replacement would also have applied -- "the next
  // time" means this one.
  const armed = state.turnEffects.findIndex(
    (e) => e.k === 'nextDeath' && e.object === victim.id,
  );
  if (armed >= 0) {
    const effect = state.turnEffects[armed] as Extract<TurnEffect, { k: 'nextDeath' }>;
    state.turnEffects.splice(armed, 1);
    const source = state.objects[effect.source];
    if (source) {
      victim.damage = 0;
      runEffects({
        state, rules, source, controller: effect.player,
        chosen: [victim.id], lastDealKilled: false,
        revealed: [], didIt: false, paidAdditionalCost: false,
      }, effect.effects);
      state.version++;
      return true;
    }
  }

  for (const source of boardObjects(state)) {
    for (const effect of activePassives(state, rules, source)) {
      if (effect.p !== 'replaceDeath') continue;
      if (!matchesFilter(state, rules, victim, effect.filter, source)) continue;

      // 704. "Its controller may pay ..." -- the offer belongs to the dying
      // unit's controller. An uncontrolled battlefield has no controller of its
      // own to make it.
      const actor = effect.byVictimController ? victim.controller : source.controller;
      const ctx: EffectContext = {
        state, rules, source, controller: actor,
        chosen: [victim.id], lastDealKilled: false,
        revealed: [], didIt: false, paidAdditionalCost: false,
      };
      if (effect.cond && !conditionHolds(ctx, effect.cond)) continue;

      // 204.3. A replacement with a price applies only if it can be paid.
      if (effect.cost) {
        const owner = actor;
        const pool = state.players[owner].pool;
        const resource = {
          energy: effect.cost.energy ?? 0,
          power: effect.cost.power ?? 0,
          powerDomains: rules.index.get(source.card).domains,
        };
        if (!canPay(pool, resource)) continue;
        if (!canPayCostParts(state, rules, source, owner, effect.cost)) continue;
        pay(pool, resource);
        payCostParts(state, rules, source, owner, effect.cost);
      }

      // Clear the damage first: the instructions may leave it on the board,
      // and it must not be caught by the very next lethal-damage sweep.
      victim.damage = 0;
      runEffects(ctx, effect.effects);
      state.version++;
      return true;
    }
  }
  return false;
}

/**
 * 363.3. Runs the effects that were waiting for this moment, and drops them.
 *
 * A "next phase" effect must not fire in the phase it was scheduled during, so
 * anything created this turn is held back until the turn number moves on.
 */
function runDelayed(
  state: GameState, rules: Rules, player: PlayerId,
  when: 'startOfPhase' | 'endOfTurn' | 'hold', phase?: Phase,
): void {
  const ready: DelayedEffect[] = [];
  const keep: DelayedEffect[] = [];

  for (const d of state.delayed) {
    const matches = d.player === player && d.when === when
      && (phase === undefined || d.phase === phase)
      // A hold can happen on the same turn the effect was armed, and often
      // does; only the phase forms have to wait for the turn to move on.
      && (when !== 'startOfPhase' || d.createdTurn < state.turn);
    if (matches) ready.push(d);
    else keep.push(d);
  }
  state.delayed = keep;

  for (const d of ready) {
    const source = state.objects[d.source];
    if (!source) continue;
    const ctx: EffectContext = {
      state, rules, source, controller: d.player,
      chosen: d.chosen ? [...d.chosen] : [], lastDealKilled: false,
      revealed: [], didIt: false, paidAdditionalCost: false,
    };
    runEffects(ctx, d.effects as Instruction[]);
  }
}

function pushTrigger(
  state: GameState, rules: Rules, source: GameObject,
  ability: Extract<Ability, { kind: 'triggered' }>,
  // "That player channels 1 rune": the effect belongs to whoever's phase it
  // was, which for an uncontrolled battlefield is not the source's controller.
  actor: PlayerId = source.controller,
): void {
  const item: ChainItem = {
    id: state.nextChainId++, kind: 'ability', source: source.id,
    controller: actor, finalized: true,
    targets: triggerTargets(state, rules, source, ability.effects, actor),
    destination: null, paidAccelerate: false, paidAdditional: false,
    effects: ability.effects, fromTrigger: true,
    addsResources: onlyAddsResources(ability.effects),
    sourceCard: source.card,
  };
  state.chain.push(item);
  state.openState = 'closed';
  state.passes = 0;
  state.version++;
}

const onlyAddsResources = (effects: readonly Instruction[]): boolean =>
  effects.length > 0 && effects.every((e) => e.op === 'addResource');

/**
 * Picks targets for an ability nobody is going to be asked about -- triggers
 * and AI plays. Enemies are chosen biggest-first because that is almost always
 * the right answer for removal, friendlies likewise for buffs.
 */
/**
 * The candidate pool for one `choose` selector, best-first.
 *
 * Split out of `autoTargets` so that enumeration and the canonical pick share
 * one definition of what is targetable -- if they drifted, the UI would offer
 * a target the engine then refused.
 */
function choicePool(
  state: GameState, rules: Rules, source: GameObject,
  sel: Extract<Selector, { s: 'choose' }>, controller: PlayerId,
  already: GameObject | undefined,
): GameObject[] {
  return candidates(state, rules, source, sel.of, already)
    .filter((o) => (sel.of.side === 'friendly' ? o.controller === controller
      : sel.of.side === 'enemy' ? o.controller !== controller : true))
    .sort((a, b) => effectiveMight(state, rules, b) - effectiveMight(state, rules, a));
}

/** The groups one selector may take from its pool, best-first. */
function choiceGroups(
  sel: Extract<Selector, { s: 'choose' }>, pool: GameObject[], branch: boolean,
  might: (o: GameObject) => number,
): ObjectId[][] {
  // "Up to one enemy unit at each location": one pick per place, taken
  // biggest-first within each, and however many places there happen to be.
  if (sel.perLocation) {
    const taken = new Set<string>();
    const group: ObjectId[] = [];
    for (const o of pool) {
      const key = o.location
        ? `${o.location.kind}:${'index' in o.location ? o.location.index : o.location.player}`
        : 'none';
      if (taken.has(key)) continue;
      taken.add(key);
      group.push(o.id);
    }
    return group.length > 0 ? [group] : [];
  }

  // "Two units at the same battlefield": the picks constrain each other, so
  // the first one decides where the rest have to come from.
  if (sel.sameLocation) {
    const first = pool[0];
    if (!first) return [];
    const together = pool.filter((o) => sameLocation(o.location, first.location));
    return [together.slice(0, sel.count ?? 1).map((o) => o.id)];
  }

  // "Any number of units with a total [M] of 8 or less" caps the group, not
  // each member. Taken biggest-first, which is what a player does with a
  // budget, and stopping once the next one would go over.
  if (sel.totalMightAtMost !== undefined) {
    let spent = 0;
    const group: ObjectId[] = [];
    for (const o of pool) {
      const m = might(o);
      if (spent + m > sel.totalMightAtMost) continue;
      spent += m;
      group.push(o.id);
    }
    return group.length > 0 ? [group] : [];
  }

  const count = sel.count ?? 1;
  // A single target is the case worth branching on: it is most of the corpus,
  // and it is the one a player expects to pick. Groups of several stay
  // canonical -- enumerating every pair of targets would multiply the action
  // list without teaching anyone anything.
  if (branch && count === 1) return pool.map((o) => [o.id]);
  const group = pool.slice(0, count).map((o) => o.id);
  return group.length > 0 ? [group] : [];
}

/**
 * Every target set this ability could legally be played with, best-first.
 *
 * The engine cannot stop mid-resolution to ask a question, so a choice that
 * has to be made *before* the ability is on the chain is made by enumerating
 * it: one action per target, and picking the action is picking the target.
 * That is what lets a player aim a spell instead of watching the engine aim it
 * for them, and it costs the AI nothing -- it already searches the list, so a
 * wider list is a better search rather than a slower one.
 *
 * Targets have to be settled this early because they are priced: 809 Deflect
 * taxes a play for what it aims at, and 356 discounts one for the same reason,
 * so the offer and the payment must agree about what this play chooses.
 *
 * Returns `[[]]` when there is nothing to choose, and `[]` when the ability
 * needs a target and none exists -- callers use that to withhold the action.
 */
export function targetVariants(
  state: GameState, rules: Rules, source: GameObject,
  effects: readonly Instruction[], controller: PlayerId,
  limit = 12,
): ObjectId[][] {
  const sels = choiceSelectors(effects);
  if (sels.length === 0) return [[]];

  let variants: ObjectId[][] = [[]];
  for (const sel of sels) {
    const next: ObjectId[][] = [];
    for (const soFar of variants) {
      // Selectors are picked in order, and a later one can refer to an earlier
      // pick ("an enemy unit at its location"), so what has been chosen so far
      // is handed down.
      const already = soFar[0] !== undefined ? state.objects[soFar[0]] : undefined;
      const pool = choicePool(state, rules, source, sel, controller, already);
      const groups = choiceGroups(
        sel, pool, variants.length < limit, (o) => effectiveMight(state, rules, o),
      );
      // A selector with nothing to point at contributes nothing rather than
      // killing the play: "deal 2 damage to a unit and draw a card" is still
      // playable for the draw when the board is empty.
      if (groups.length === 0) { next.push(soFar); continue; }
      for (const group of groups) next.push([...soFar, ...group]);
    }
    variants = next.slice(0, limit);
    if (variants.length === 0) return [];
  }
  return variants.filter((v) => v.length > 0);
}

/**
 * Whether any triggered ability on the board could stop to ask for a target.
 *
 * A cheap structural test, not an exact one: it exists only to keep the drain's
 * snapshot off the common path, and a false positive costs one clone.
 */
function triggersMayAsk(state: GameState, rules: Rules): boolean {
  const asks = (o: GameObject): boolean => {
    for (const ability of abilitiesOn(state, rules, o)) {
      if (ability.kind !== 'triggered') continue;
      if (choiceSelectors(ability.effects).length > 0) return true;
    }
    return false;
  };
  for (const o of boardObjects(state)) if (asks(o)) return true;
  // A permanent still on the chain counts: `enterBoard` fires its own "when
  // you play me" the moment it lands, which is inside this same settle and
  // before it has ever appeared in `boardObjects`.
  for (const item of state.chain) {
    const source = state.objects[item.source];
    if (source && asks(source)) return true;
  }
  return false;
}

/**
 * Whether a snapshot exists to roll back to, and so whether a trigger may stop
 * to ask at all.
 *
 * Module-level because threading it through the whole settle -- cleanup, the
 * Might sweep, the drain, the phase and combat steps -- would touch a dozen
 * signatures to carry one bit. `advance` is the only thing that sets it, is
 * not re-entrant, and always clears it.
 *
 * The point of the flag is that a missed case degrades instead of crashing:
 * `triggerTargets` throws only when the throw will be caught, and otherwise
 * falls back to the canonical pick. An escaping `NeedsChoice` would leave the
 * engine and take the match with it, which is far worse than a target chosen
 * for you.
 */
let askArmed = false;

/**
 * 359.3. Targets for a trigger going on the chain -- asking when it is a real
 * choice.
 *
 * A trigger is not an offer anyone can decline, so there is no action to
 * enumerate and no play to price; but *which* enemy unit Ahri shrinks is still
 * the controller's decision, and picking biggest-Might-first for them made
 * every targeted trigger in the game play itself. When only one target set is
 * legal there is nothing to ask, and the canonical pick stands.
 */
function triggerTargets(
  state: GameState, rules: Rules, source: GameObject,
  effects: readonly Instruction[], controller: PlayerId,
): ObjectId[] {
  const out: ObjectId[] = [];
  for (const sel of choiceSelectors(effects)) {
    const already = out[0] !== undefined ? state.objects[out[0]] : undefined;
    const pool = choicePool(state, rules, source, sel, controller, already);
    // Branching, so a single target yields one group per candidate -- that is
    // the list to ask from. A group of several stays canonical, the same way a
    // play's targets do: enumerating every pair teaches nobody anything.
    const groups = choiceGroups(
      sel, pool, true, (o) => effectiveMight(state, rules, o),
    );
    if (groups.length === 0) continue;
    if (groups.length === 1) { out.push(...groups[0]!); continue; }

    // Answers accumulate across replays, so a question already settled returns
    // at once and only the newest one suspends.
    const logged = state.triggerChoiceLog[state.triggerChoiceCursor];
    if (logged !== undefined) {
      state.triggerChoiceCursor++;
      // A stale answer falls back to the canonical pick rather than throwing:
      // the replay has to reach the same number of questions as the first pass.
      out.push(...(groups.find((g) => g[0] === logged) ?? groups[0]!));
      continue;
    }
    // Nothing to roll back to means the question cannot be asked safely; the
    // canonical pick is the old behaviour and is strictly better than a throw
    // that leaves the engine.
    if (!askArmed) { out.push(...groups[0]!); continue; }
    throw new NeedsChoice({
      player: controller,
      for: 'trigger',
      prompt: 'Choose a target for',
      source: source.id,
      // One entry per target set, named by its first member -- the same shape
      // the client already answers for a play, whose targets are enumerated.
      cards: groups.map((g) => g[0]!).filter((id): id is ObjectId => id !== undefined),
    });
  }
  return out;
}

export function autoTargets(
  state: GameState, rules: Rules, source: GameObject,
  effects: readonly Instruction[], controller: PlayerId,
): ObjectId[] {
  const out: ObjectId[] = [];
  for (const sel of choiceSelectors(effects)) {
    const already = out[0] !== undefined ? state.objects[out[0]] : undefined;
    const pool = choicePool(state, rules, source, sel, controller, already);
    const group = choiceGroups(
      sel, pool, false, (o) => effectiveMight(state, rules, o),
    )[0];
    if (group) out.push(...group);
  }
  return out;
}

/** Every `choose` selector in an instruction tree, in order. */
export function choiceSelectors(
  effects: readonly Instruction[],
): Extract<Selector, { s: 'choose' }>[] {
  const out: Extract<Selector, { s: 'choose' }>[] = [];
  const visitSelector = (sel: Selector | undefined) => {
    if (sel && sel.s === 'choose') out.push(sel);
  };
  const walk = (list: readonly Instruction[]) => {
    for (const i of list) {
      const anyInstr = i as { target?: Selector; who?: Selector };
      visitSelector(anyInstr.target);
      if (i.op === 'if') { walk(i.then); if (i.else) walk(i.else); }
      if (i.op === 'repeat') walk(i.body);
    }
  };
  walk(effects);
  return out;
}

// -- legality ----------------------------------------------------------------

/**
 * Everything the waiting player may do right now. This is the only source of
 * truth for legality: `applyAction` re-checks against this list, so a UI and an
 * AI cannot diverge on what is allowed.
 */
export function legalActions(state: GameState, rules: Rules): Action[] {
  const w = state.waiting;
  if (w.kind === 'gameOver' || w.kind === 'none') return [];

  if (w.kind === 'mulligan') return mulliganOptions(state, w.player);

  // 355.3 / 424. An effect stopped to ask. One action per answer, which is the
  // same shape everything else takes -- so a client picks an answer the way it
  // picks a play, and the AI searches them the way it searches anything else.
  if (w.kind === 'choose') return choiceOptions(state);

  if (w.kind === 'assignDamage') {
    const pending = state.pendingAssignment;
    if (!pending) return [];
    return [{ t: 'assignDamage', order: defaultDamageOrder(state, rules, pending.targets) }];
  }

  const player = w.player;
  const out: Action[] = [{ t: 'pass' }, { t: 'concede' }];

  // 316.9. Only the turn player, in a neutral open main phase, can end the turn.
  const neutralOpenMain = state.phase === 'main' && state.showdownState === 'neutral'
    && state.openState === 'open' && player === state.turnPlayer;
  if (neutralOpenMain) out.push({ t: 'endTurn' });

  for (const card of playableCards(state, rules, player)) out.push(...card);
  for (const a of activatableAbilities(state, rules, player)) out.push(a);
  for (const a of hideActions(state, rules, player)) out.push(a);
  if (neutralOpenMain) out.push(...standardMoves(state, rules, player));

  return out;
}

function mulliganOptions(state: GameState, player: PlayerId): Action[] {
  const hand = zoneList(state, 'hand', player);
  const out: Action[] = [{ t: 'mulligan', cards: [] }];
  // 117.1. Up to two cards may be set aside; enumerate every subset.
  for (let i = 0; i < hand.length; i++) {
    out.push({ t: 'mulligan', cards: [hand[i]!] });
    for (let j = i + 1; j < hand.length; j++) {
      out.push({ t: 'mulligan', cards: [hand[i]!, hand[j]!] });
    }
  }
  return out;
}

/**
 * 331 / 343. What may be played depends on the two state axes: a closed state
 * needs Reaction, a showdown needs Action, and a neutral open main phase on
 * your own turn allows anything.
 */
function canPlayNow(
  state: GameState, rules: Rules, obj: GameObject, player: PlayerId,
): boolean {
  const isTurnPlayer = player === state.turnPlayer;
  // 349. "I can't be played on your first, second, or third turns." Printed on
  // the card and read from its own abilities, not from the board: the card is
  // still in hand, where no board-read passive would ever find it.
  for (const ability of abilitiesOf(rules, obj.card)) {
    if (ability.kind !== 'passive') continue;
    for (const e of ability.effects) {
      if (e.p === 'cantPlayBeforeTurn' && state.players[player].turnsTaken < e.turn) {
        return false;
      }
    }
  }
  if (isRestricted(state, rules, 'playCards', player)) return false;
  // 133.8. "Opponents can't play spells with that name." Checked with the card
  // as the subject, because the restriction is about which card this is.
  if (isRestricted(state, rules, 'playNamedSpell', player, { subject: obj })) return false;
  // A prohibition can name one kind of card: "its controller can't play spells
  // this turn" stops only those.
  const stopped = state.turnEffects.some((e) =>
    e.k === 'cantPlayCards' && e.player === player
    && (!e.only || matchesTypeWord(rules.index.get(obj.card).type, e.only)));
  if (stopped) return false;
  if (state.openState === 'closed') return hasKeywordNow(state, rules, obj, 'reaction');
  if (state.showdownState === 'showdown') {
    return hasKeywordNow(state, rules, obj, 'action')
      || hasKeywordNow(state, rules, obj, 'reaction');
  }
  return isTurnPlayer && state.phase === 'main';
}

/** 355.3 / 424. The answers the pending question will accept. */
function choiceOptions(state: GameState): Action[] {
  const ask = state.pendingChoice;
  if (!ask) return [];
  if (ask.modes !== undefined) {
    return ask.modes.map((i) => ({ t: 'choose', pick: i }));
  }
  // 133.8. Naming: `pick` is an index into the words on offer.
  if (ask.names !== undefined) {
    return ask.names.map((_, i) => ({ t: 'choose', pick: i }));
  }
  // A card that has left the zone it was offered from is no longer an answer.
  const cards: Action[] = (ask.cards ?? [])
    .filter((id) => state.objects[id] !== undefined)
    .map((id) => ({ t: 'choose', pick: id }));
  // 436.1.a. "Recycles any number of them" includes none, so an optional
  // choice always has a way out. `DECLINE` is a sentinel, never an object id.
  if (ask.decline !== undefined) cards.push({ t: 'choose', pick: DECLINE });
  return cards;
}

function playableCards(state: GameState, rules: Rules, player: PlayerId): Action[][] {
  const out: Action[][] = [];
  const pool = state.players[player].pool;

  const sources = [
    ...zoneList(state, 'hand', player),
    // 108.3.d. The Chosen Champion is played from its own zone.
    ...zoneList(state, 'championZone', player),
  ];

  // 829.1.b. "You may play this from your trash for its Flow cost."
  for (const id of zoneList(state, 'trash', player)) {
    const card = state.objects[id];
    if (!card) continue;
    const flow = scriptFor(rules, card.card).flowCost;
    if (!flow) continue;
    if (!canPlayNow(state, rules, card, player)) continue;
    const effects = spellEffects(rules, card.card);
    // 829. A discount that applies to the Flow price rather than the printed
    // one, which this play never pays.
    let flowOff = 0;
    for (const o of boardObjects(state)) {
      if (o.controller !== player) continue;
      for (const e of activePassives(state, rules, o)) {
        if (e.p === 'flowCostModifier') {
          flowOff += e.energy;
          if (e.min !== undefined) flowOff = Math.min(flowOff, flow.energy - e.min);
        }
      }
    }
    for (const targets of targetVariants(state, rules, card, effects, player)) {
      const flowCost = withDeflect({
        energy: Math.max(0, flow.energy - flowOff),
        power: flow.power,
        powerDomains: flow.domain && flow.domain !== 'any'
          ? [flow.domain]
          : rules.index.get(card.card).domains,
      }, deflectCost(state, rules, targets, player));
      if (!canPay(pool, flowCost, 'spell', inShowdown(state))) continue;
      out.push([{ t: 'play', object: card.id, targets, flow: true }]);
    }
  }

  // 356.4. "You may play this from your trash for [A]" -- an offer a spell left
  // behind about itself as it resolved. Enumerated like the Flow play above:
  // the card is in the trash, and the price is the one it named.
  for (const e of state.turnEffects) {
    if (e.k !== 'playFromTrash' || e.player !== player) continue;
    const card = state.objects[e.object];
    if (!card || card.zone.kind !== 'trash' || card.owner !== player) continue;
    if (!canPlayNow(state, rules, card, player)) continue;
    const effects = spellEffects(rules, card.card);
    for (const targets of targetVariants(state, rules, card, effects, player)) {
      const price = withDeflect({
        energy: e.price.energy,
        power: e.price.power,
        powerDomains: e.price.domain && e.price.domain !== 'any'
          ? [e.price.domain]
          : rules.index.get(card.card).domains,
      }, deflectCost(state, rules, targets, player));
      if (!canPay(pool, price, 'spell', inShowdown(state))) continue;
      out.push([{ t: 'play', object: card.id, targets, fromTrashOffer: true }]);
    }
  }

  // 356.4. "You may play me for [B]" -- an alternative price, from whichever
  // zone the card names. Enumerated beside the printed play rather than in
  // place of it: both are legal, and the player picks.
  for (const zone of ['hand', 'trash'] as const) {
    for (const id of zoneList(state, zone, player)) {
      const card = state.objects[id];
      if (!card) continue;
      const alt = scriptFor(rules, card.card).altCost;
      if (!alt || alt.from !== zone) continue;
      if (alt.cond
        && testStaticCondition(state, rules, card, player, alt.cond) !== true) continue;
      if (!canPlayNow(state, rules, card, player)) continue;
      const effects = spellEffects(rules, card.card);
      const def = rules.index.get(card.card);
      for (const targets of targetVariants(state, rules, card, effects, player)) {
        const price = withDeflect({
          energy: alt.energy,
          power: alt.power,
          powerDomains: alt.domain && alt.domain !== 'any'
            ? [alt.domain]
            : def.domains,
        }, deflectCost(state, rules, targets, player));
        if (!canPay(pool, price, 'spell', inShowdown(state))) continue;
        out.push([{
          t: 'play', object: card.id, targets, alt: true,
          ...(isUnitType(def.type) ? { to: baseLocation(player) } : {}),
        }]);
      }
    }
  }

  // 811.1.b. From the turn after it was hidden, a facedown card gains
  // [Reaction] and may be played ignoring its base cost.
  for (const bf of state.battlefields) {
    // 811. A battlefield can stop face-down cards being turned up on it.
    if (isRestricted(state, rules, 'revealHidden', player,
      { at: battlefieldLocation(bf.index) })) continue;
    for (const card of facedownAt(state, bf.index, player)) {
      if (card.enteredTurn >= state.turn) continue;
      if (bf.controller !== player) continue;
      const def = rules.index.get(card.card);
      const effects = isSpellType(def.type) ? spellEffects(rules, card.card) : [];
      for (const targets of targetVariants(state, rules, card, effects, player)) {
        if (isUnitType(def.type)) {
          out.push([{ t: 'play', object: card.id, to: battlefieldLocation(bf.index), targets }]);
        } else {
          out.push([{ t: 'play', object: card.id, targets }]);
        }
      }
    }
  }

  for (const id of sources) {
    const card = state.objects[id];
    if (!card) continue;
    const def = rules.index.get(card.card);
    if (!canPlayNow(state, rules, card, player)) continue;

    // 809. Targeting something with Deflect is a mandatory additional cost,
    // so the price depends on what this play would choose -- and 356 hands
    // back a discount for the same reason. Both are written as one function of
    // the choice, because a play that aims somewhere else is a different
    // price, and the offer and the payment have to agree about which.
    const priceFor = (chosen: readonly ObjectId[]): Cost => {
      const taxed = withDeflect(
        totalCost(state, rules, def, player),
        ignoresDeflect(rules, card.card)
          ? 0 : deflectCost(state, rules, chosen as ObjectId[], player),
      );
      return discounted(
        discounted(
          discounted(taxed, chosenDiscount(state, rules, chosen as ObjectId[], player, taxed)),
          { energy: firstEachTurnDiscount(state, rules, player, 'card', def), power: 0 },
        ),
        choosingDiscount(state, rules, card.card, chosen as ObjectId[], card),
      );
    };
    const chosen = autoTargets(state, rules, card, spellEffects(rules, card.card), player);
    const cost = priceFor(chosen);
    if (!canPay(pool, cost, purposeOfCard(def.type), inShowdown(state))) continue;

    // 356.2. An optional additional cost is a real choice, so both the plain
    // play and the one that pays it are offered.
    const extra = additionalCostOf(rules, card.card);
    const extraResource = extra ? (() => {
      const raw = { energy: extra.cost.energy ?? 0, power: extra.cost.power ?? 0 };
      // 356.2. Priced with the same discount the payment will take, or a card
      // only affordable *with* the discount is never offered.
      const off = extraCostDiscount(state, rules, player, raw);
      return {
        energy: Math.max(0, raw.energy - off.energy),
        power: Math.max(0, raw.power - off.power),
        powerDomains: rules.index.get(card.card).domains,
      };
    })() : null;
    // Only offer the paying variant when the extra cost can actually be met,
    // together with the card's own cost.
    // 356.2. Taking the extra cost can make the card cheaper, so the variant
    // has to be priced with its discount -- otherwise a card that is only
    // affordable *with* the extra cost is never offered at all.
    // 356.2. And so can the *card* the extra cost consumes: "I cost [1] less
    // for each Energy it costs". Priced from the same candidate the payment
    // will take, so the offer and the payment cannot disagree about how much
    // the play ends up costing.
    const doomed = extra?.cost.sacrifice
      ? sacrificeCandidates(state, rules, card, extra.cost.sacrifice)
        .slice(0, extra.cost.sacrifice.count)
      : [];
    const withExtra = discounted(
      discounted(cost, additionalCostDiscount(rules, card.card)),
      sacrificedDiscount(rules, card.card, doomed),
    );
    const canTakeExtra = extra !== null && extraResource !== null
      && canPayCostParts(state, rules, card, player, extra.cost)
      && (extra.variable
        // 356.2. A variable extra is affordable when *some* number of it is;
        // `variableSpend` settles which, and the payment uses the same answer.
        ? variableSpend(state, rules, player, def, extra, cost) !== null
        : canPay(pool, {
          energy: withExtra.energy + extraResource.energy,
          power: withExtra.power + extraResource.power,
          powerDomains: withExtra.powerDomains,
        }, purposeOfCard(def.type), inShowdown(state)));
    // A mandatory extra cost that cannot be met means the card is unplayable,
    // rather than playable and then refused.
    if (extra && !extra.optional && !canTakeExtra) continue;

    const variants: (boolean | undefined)[] = extra?.optional && canTakeExtra
      ? [undefined, true] : [undefined];

    // 820. "You may pay the additional cost to repeat this spell's effect."
    // Offered beside the plain play, priced with the extra on top.
    const repeatCost = repeatCostFor(state, rules, card, player) ?? undefined;
    const repeatable = (base: Cost): boolean => repeatCost !== undefined && canPay(pool, {
      energy: base.energy + repeatCost.energy,
      power: base.power + repeatCost.power,
      powerDomains: base.powerDomains,
    }, purposeOfCard(def.type), inShowdown(state));

    const actions: Action[] = [];
    if (isUnitType(def.type)) {
      // 355.2.a. A unit enters at your base or a battlefield you control;
      // Ambush also allows a battlefield where you already have units (822).
      for (const to of unitDestinations(state, rules, card, player)) {
        for (const additional of variants) {
          actions.push(additional === undefined
            ? { t: 'play', object: id, to, targets: [] }
            : { t: 'play', object: id, to, targets: [], additional });
        }
        if (def.keywords.includes('accelerate')) {
          const accel = accelerateCost(state, rules, def, player);
          if (canPay(pool, accel, purposeOfCard(def.type), inShowdown(state))) {
            actions.push({ t: 'play', object: id, to, targets: [], accelerate: true });
          }
        }
      }
    } else if (def.type === 'gear') {
      // 148.1.a.1. Gear is played to base unless something says otherwise.
      for (const additional of variants) {
        actions.push(additional === undefined
          ? { t: 'play', object: id, to: baseLocation(player), targets: [] }
          : { t: 'play', object: id, to: baseLocation(player), targets: [], additional });
      }
    } else if (isSpellType(def.type)) {
      const effects = spellEffects(rules, card.card);
      // 355.8. A spell needs valid choices for all its targets to be played --
      // and one action per choice is what lets the player aim it, since the
      // chain cannot stop and ask once the spell is on it.
      for (const targets of targetVariants(state, rules, card, effects, player)) {
        const priced = priceFor(targets);
        if (!canPay(pool, priced, purposeOfCard(def.type), inShowdown(state))) continue;
        for (const additional of variants) {
          actions.push(additional === undefined
            ? { t: 'play', object: id, targets }
            : { t: 'play', object: id, targets, additional });
        }
        if (repeatable(priced)) actions.push({ t: 'play', object: id, targets, repeat: true });
      }
    }
    // 356.2. An offer another permanent is making on this card, which is a
    // different price *and* a different destination -- so it is its own action
    // rather than a flag on one of the above.
    for (const offer of grantedPlayOffers(state, rules, card, player)) {
      const priced = {
        energy: cost.energy + offer.cost.energy,
        power: cost.power + offer.cost.power,
        powerDomains: cost.powerDomains,
      };
      if (!canPay(pool, priced, purposeOfCard(def.type), inShowdown(state))) continue;
      actions.push({
        t: 'play', object: id, to: offer.to, targets: [], grantedBy: offer.by.id,
      });
    }

    if (actions.length) out.push(actions);
  }
  return out;
}

/**
 * 356.4. The standing offer to play this card out of the trash, if any.
 *
 * Looked up rather than passed around, so the enumeration, the legality check
 * and the payment all read the same turn effect and cannot disagree about the
 * price or about whether the offer is still there.
 */
function trashOffer(
  state: GameState, card: GameObject, player: PlayerId,
): Extract<TurnEffect, { k: 'playFromTrash' }> | null {
  return state.turnEffects.find(
    (e): e is Extract<TurnEffect, { k: 'playFromTrash' }> =>
      e.k === 'playFromTrash' && e.player === player && e.object === card.id,
  ) ?? null;
}

/**
 * 809.1.c. Adds the Deflect tax to a cost.
 *
 * The extra Power may be of any domain (809.1.c.1). `powerDomains` is the set
 * of domains the pips may be paid in and an empty set means "any", so a cost
 * that already names a domain keeps it -- the tax is not domain-restricted but
 * it is paid alongside pips that are, and the engine does not model the two
 * pools separately.
 */
/**
 * 355.9. Announces the act of choosing, for both spells and abilities.
 *
 * "When you choose a friendly unit ..." watches the targeting itself, not the
 * effect that follows it -- and it fired only for activated abilities, so a
 * spell that chose the same unit was invisible to it. The running count of
 * enemy things chosen is kept here too: no board state records it.
 */
function noteChoices(state: GameState, player: PlayerId, targets: readonly ObjectId[]): void {
  for (const id of targets) {
    state.pendingTriggers.push({ on: 'chosen', player, subject: id });
    const o = state.objects[id];
    if (o && o.controller !== player) state.players[player].chosenEnemyThisTurn++;
  }
}

function withDeflect(cost: Cost, owed: number): Cost {
  return owed === 0 ? cost : { ...cost, power: cost.power + owed };
}

/**
 * Applies a discount to a cost, never below free.
 *
 * `powerDomains` is the set of domains the pips may be paid in, not one entry
 * per pip, so it is carried through untouched: shortening it would widen a
 * single-domain cost into one any domain can pay.
 */
function discounted(cost: Cost, off: { energy: number; power: number }): Cost {
  return {
    energy: Math.max(0, cost.energy - off.energy),
    power: Math.max(0, cost.power - off.power),
    powerDomains: cost.powerDomains,
  };
}

/** 805.1.a. Accelerate is an optional additional cost of [1][C]. */
function accelerateCost(
  state: GameState, rules: Rules, def: ReturnType<Rules['index']['get']>, player: PlayerId,
) {
  const base = totalCost(state, rules, def, player);
  return {
    energy: base.energy + 1,
    power: base.power + 1,
    powerDomains: base.powerDomains,
  };
}

/**
 * 356.2. Offers other permanents are making on this card's play.
 *
 * "Any player may pay [A][A] as an additional cost to play a Dragon. If they
 * do, they play it to this battlefield." The price and the destination are the
 * same offer, so both are read off the offering permanent rather than derived
 * separately by the enumeration and the payment.
 */
function grantedPlayOffers(
  state: GameState, rules: Rules, card: GameObject, player: PlayerId,
): { by: GameObject; cost: Cost; to: Location }[] {
  const out: { by: GameObject; cost: Cost; to: Location }[] = [];
  for (const other of boardObjects(state)) {
    if (other.location?.kind !== 'battlefield') continue;
    for (const e of activePassives(state, rules, other)) {
      if (e.p !== 'grantsAdditionalCost') continue;
      if (e.who === 'you' && other.controller !== player) continue;
      if (e.who === 'opponents' && other.controller === player) continue;
      if (!matchesFilter(state, rules, card, e.filter, other)) continue;
      out.push({
        by: other,
        cost: { energy: e.energy, power: e.power, powerDomains: [] },
        to: battlefieldLocation(other.location.index),
      });
    }
  }
  return out;
}

function unitDestinations(
  state: GameState, rules: Rules, card: GameObject, player: PlayerId,
): Location[] {
  const ambush = hasKeywordNow(state, rules, card, 'ambush');

  // 822. "I may be played to an open battlefield" and friends widen this.
  const widen = new Set<string>();
  for (const e of activePassives(state, rules, card)) {
    if (e.p === 'playableTo' && !e.filter) widen.add(e.where);
  }
  // The aura form: another permanent widens it for a whole class of units.
  for (const other of boardObjects(state)) {
    for (const e of activePassives(state, rules, other)) {
      if (e.p !== 'playableTo' || !e.filter) continue;
      if (matchesFilter(state, rules, card, e.filter, other)) widen.add(e.where);
    }
  }

  // 471. "Play me only to a battlefield you conquered this turn. (You can't
  // play me anywhere else.)" The parenthesis is the operative half, and the
  // base is somewhere else: it used to be seeded unconditionally, so the
  // narrowing correctly filtered the battlefields and then left the base
  // standing as a fallback the card explicitly forbids.
  const onlyConquered = widen.has('onlyConqueredThisTurn');
  const out: Location[] = onlyConquered ? [] : [baseLocation(player)];

  for (const bf of state.battlefields) {
    const mine = unitsOf(state, rules.index, player, battlefieldLocation(bf.index));
    const open = bf.controller === null;
    const enemy = bf.controller !== null && bf.controller !== player;
    if (isRestricted(state, rules, 'playUnitsHere', player,
      { at: battlefieldLocation(bf.index) })) continue;
    // 463. "I can be played to a battlefield you're attacking": the combat
    // has to be live and this player has to be the attacker in it.
    const attackingHere = state.combat !== null
      && state.combat.battlefield === bf.index
      && state.combat.attacker === player;
    // 471. A narrowing, not a widening: with it the card goes nowhere else.
    if (widen.has('onlyConqueredThisTurn')
      && !bf.conqueredThisTurn.includes(player)) continue;
    // 152. "... if an enemy unit is alone there": exactly one unit at the
    // battlefield, and not this player's.
    const there = boardObjects(state).filter((o) =>
      isUnitType(rules.index.get(o.card).type)
      && sameLocation(o.location, battlefieldLocation(bf.index)));
    const loneEnemy = there.length === 1 && there[0]!.controller !== player;
    // 356.2. "You may play me to its battlefield": `its` is the unit killed to
    // pay this card's additional cost, and that unit is chosen as the card is
    // played -- so the destination is open wherever a unit the cost could name
    // is standing, and the payment then takes one from there.
    const sacrificeHere = widen.has('sacrificedBattlefield')
      && (() => {
        const extra = additionalCostOf(rules, card.card);
        const filter = extra?.cost.sacrifice?.filter;
        if (!filter) return false;
        return there.some((o) => o.controller === player
          && matchesFilter(state, rules, o, filter, card));
      })();

    if (bf.controller === player
      || (ambush && mine.length > 0)
      || (widen.has('anyBattlefield'))
      || (widen.has('openBattlefield') && open)
      || (widen.has('enemyBattlefield') && enemy)
      || (widen.has('battlefieldWithLoneEnemy') && loneEnemy)
      || (widen.has('attackingBattlefield') && attackingHere)
      || sacrificeHere) {
      out.push(battlefieldLocation(bf.index));
    }
  }
  return out;
}

/** 145.2 / 376. Activated abilities of permanents the player controls. */
function activatableAbilities(state: GameState, rules: Rules, player: PlayerId): Action[] {
  const out: Action[] = [];
  const pool = state.players[player].pool;

  for (const o of boardObjects(state)) {
    if (o.controller !== player) continue;
    // 363. Granted abilities are used exactly like printed ones, so the
    // enumeration has to see both.
    for (const ability of abilitiesOn(state, rules, o)) {
      if (ability.kind !== 'activated') continue;

      const timingOk = ability.timing === 'reaction'
        || (ability.timing === 'action' && state.openState === 'open')
        || (ability.timing === 'main' && state.openState === 'open'
          && state.showdownState === 'neutral' && player === state.turnPlayer
          && state.phase === 'main');
      if (!timingOk) continue;

      // 141.1.a.1. Some abilities only work while their source is deployed.
      if (o.location?.kind !== 'battlefield'
        && activePassives(state, rules, o).some((e) => e.p === 'abilityRequiresBattlefield')) {
        continue;
      }

      // 371. One use per turn, tracked on the object rather than the card so
      // two copies of the same permanent are independent.
      if (ability.once && o.usedThisTurn.includes(ability.key)) continue;

      // 371. "Use only if ..." -- a restriction on using it at all.
      if (ability.useIf
        && testStaticCondition(state, rules, o, player, ability.useIf) !== true) continue;

      if (!canPayCostParts(state, rules, o, player, ability.cost)) continue;

      if ((ability.cost.recycleSelf || ability.cost.banishSelf) && o.zone.kind !== 'board') continue;
      if (ability.cost.recycleFromTrash
        && recycleCandidates(state, rules, player, ability.cost.recycleFromTrashFilter)
          .length < ability.cost.recycleFromTrash) continue;
      if (ability.cost.discard
        && discardCandidates(state, rules, player, ability.cost.discardFilter).length
          < ability.cost.discard) continue;

      // One activation per target: the ability cannot stop and ask once it is
      // on the chain, so aiming it *is* choosing which activation to take.
      for (const targets of targetVariants(state, rules, o, ability.effects, player)) {
        const resourceCost = abilityResourceCost(
          state, rules, o, ability.cost, ability.from, targets,
        );
        if (!canPay(pool, resourceCost,
          purposeOfAbility(rules.index.get(o.card).type), inShowdown(state))) continue;

        // The same list the cost was priced from, so the offer and the payment
        // cannot disagree about what this activation chooses.
        out.push({ t: 'activate', object: o.id, ability: ability.key, targets });
      }
    }
  }
  return out;
}

/**
 * 471. Looks for a replacement for scoring and, if one applies, runs it
 * instead. Returns whether the point was replaced.
 *
 * Lives here rather than in `ops.score` so `ops` need not reach back into the
 * interpreter -- resolve already imports ops, and the other direction would
 * close the loop.
 */
function replaceScore(
  state: GameState, rules: Rules, player: PlayerId,
): boolean {
  for (const source of boardObjects(state)) {
    for (const effect of activePassives(state, rules, source)) {
      if (effect.p !== 'replaceScore') continue;
      // "During their first or second turn" -- counted from that player's own
      // side, the same way `restrict`'s `untilTurn` reads.
      if (state.players[player].turnsTaken >= effect.untilTurn) continue;
      runEffects({
        state, rules, source, controller: player,
        chosen: [], lastDealKilled: false,
        revealed: [], didIt: false, paidAdditionalCost: false,
      }, effect.effects);
      state.version++;
      return true;
    }
  }
  return false;
}

/**
 * 809. Whether this card pays the Deflect tax at all.
 *
 * Read off the card's own abilities rather than the board: it is still in hand
 * when the question is asked, so nothing that walks permanents would find it.
 */
function ignoresDeflect(rules: Rules, cardId: string): boolean {
  for (const ability of abilitiesOf(rules, cardId)) {
    if (ability.kind !== 'passive') continue;
    if (ability.effects.some((e) => e.p === 'ignoreDeflect')) return true;
  }
  return false;
}

/**
 * 820. What repeating this spell would cost, or null if it cannot be repeated.
 *
 * Three sources, and a card can have none of them: the printed keyword, an
 * aura ("your spells have [Repeat] [2][P]"), and a one-shot armed earlier this
 * turn ("give the next spell you play this turn [Repeat] equal to its cost").
 * A modifier on the board can then take something off the result -- "friendly
 * [Repeat] costs cost [1] less".
 */
function repeatCostFor(
  state: GameState, rules: Rules, card: GameObject, player: PlayerId,
): { energy: number; power: number; domain?: Domain | 'any' } | null {
  const def = rules.index.get(card.card);
  if (!isSpellType(def.type)) return null;

  let base = scriptFor(rules, card.card).repeatCost ?? null;

  if (!base) {
    for (const o of boardObjects(state)) {
      if (o.controller !== player) continue;
      for (const e of activePassives(state, rules, o)) {
        if (e.p !== 'grantsRepeat') continue;
        if (!matchesFilter(state, rules, card, e.filter, o)) continue;
        base = e.price;
      }
    }
  }

  if (!base) {
    const armed = state.turnEffects.find(
      (e) => e.k === 'nextSpellRepeat' && e.player === player,
    );
    if (armed?.k === 'nextSpellRepeat') {
      base = armed.fromOwnCost
        // "equal to its cost": what this very spell costs, read now.
        ? { energy: def.cost.energy, power: def.cost.power }
        : { energy: armed.energy, power: armed.power };
    }
  }

  if (!base) return null;

  let off = 0;
  for (const o of boardObjects(state)) {
    if (o.controller !== player) continue;
    for (const e of activePassives(state, rules, o)) {
      if (e.p === 'repeatCostModifier') off += e.energy;
    }
  }
  return {
    energy: Math.max(0, base.energy - off),
    power: base.power,
    ...(base.domain ? { domain: base.domain } : {}),
  };
}

/**
 * 204. What a payment is for, so restricted Energy ("Use only to play spells")
 * knows whether it may be spent on it.
 */
function purposeOfCard(type: CardDef['type']): SpendPurpose {
  if (isSpellType(type)) return 'spell';
  if (isUnitType(type)) return 'unit';
  return type === 'gear' ? 'gear' : 'other';
}

function purposeOfAbility(type: CardDef['type']): SpendPurpose {
  if (isUnitType(type)) return 'unitAbility';
  return type === 'gear' ? 'gearAbility' : 'ability';
}

const inShowdown = (state: GameState): boolean => state.showdownState === 'showdown';

/**
 * 811.1.b. Hide: on your turn, in an Open State, pay [A] to put a card with
 * Hidden facedown at a battlefield you control that has no facedown card there
 * already.
 */
/**
 * 107.3. How many cards may be hidden at one battlefield. One by default; a
 * permanent standing there can add to it ("You may hide an additional card
 * here").
 */
function hideSlotsAt(state: GameState, rules: Rules, index: number): number {
  const at = battlefieldLocation(index);
  let slots = 1;
  for (const o of boardObjects(state)) {
    if (!sameLocation(o.location, at)) continue;
    for (const e of activePassives(state, rules, o)) {
      if (e.p === 'extraHideSlot') slots++;
    }
  }
  return slots;
}

function hideActions(state: GameState, rules: Rules, player: PlayerId): Action[] {
  if (player !== state.turnPlayer || state.openState !== 'open') return [];
  if (state.showdownState !== 'neutral' || state.phase !== 'main') return [];
  if (!canPay(state.players[player].pool, hideCostFor(state, rules, player), 'other', false)) {
    return [];
  }

  const out: Action[] = [];
  const sources = [
    ...zoneList(state, 'hand', player),
    ...zoneList(state, 'championZone', player),
  ];
  for (const id of sources) {
    const card = state.objects[id];
    if (!card || !hasKeywordNow(state, rules, card, 'hidden')) continue;
    for (const bf of state.battlefields) {
      if (bf.controller !== player) continue;
      if (facedownAt(state, bf.index).length >= hideSlotsAt(state, rules, bf.index)) continue;
      out.push({ t: 'hide', object: id, battlefield: bf.index });
    }
  }
  return out;
}

function doHide(
  state: GameState, rules: Rules, player: PlayerId,
  action: Extract<Action, { t: 'hide' }>,
): boolean {
  const card = state.objects[action.object];
  if (!card || card.controller !== player) return false;
  if (card.zone.kind !== 'hand' && card.zone.kind !== 'championZone') return false;
  if (!hasKeywordNow(state, rules, card, 'hidden')) return false;

  const bf = state.battlefields[action.battlefield];
  if (!bf || bf.controller !== player) return false;
  // Through the same count `hideActions` offers from, or a battlefield with an
  // extra slot is offered and then refused.
  if (facedownAt(state, action.battlefield).length
    >= hideSlotsAt(state, rules, action.battlefield)) return false;
  if (!pay(state.players[player].pool, hideCostFor(state, rules, player))) return false;

  moveToZone(state, rules, card, { kind: 'facedown', battlefield: action.battlefield });
  // After the move, which clears statuses on a zone change.
  card.controller = player;
  card.statuses = ['facedown'];
  state.events.push({ t: 'hide', player, object: card.id, battlefield: action.battlefield });
  state.pendingTriggers.push({ on: 'youHide', player, subject: card.id });
  state.version++;
  return true;
}

/**
 * 203. Pays a non-resource cost component from `source`. Resources are handled
 * by the caller, which knows the purpose the payment is for.
 *
 * Returns false without changing anything if any component cannot be paid.
 */
/**
 * 203.3. Whether every component of `cost` could be paid, without paying any of
 * it. `playableCards` needs this: offering a play whose extra cost cannot be
 * met means the engine proposes an action it will then refuse.
 */
function canPayCostParts(
  state: GameState, rules: Rules, source: GameObject, player: PlayerId,
  cost: ActivationCost,
): boolean {
  if (cost.exhaust && source.exhausted) return false;
  if (cost.discard
    && discardCandidates(state, rules, player, cost.discardFilter).length < cost.discard) {
    return false;
  }
  if (cost.recycleFromTrash
    && recycleCandidates(state, rules, player, cost.recycleFromTrashFilter)
      .length < cost.recycleFromTrash) return false;
  if (cost.spendXp && state.players[player].xp < cost.spendXp) return false;
  if (cost.spendBuff && !source.statuses.includes('buffed')) return false;
  // 441. Nothing to spend if it is not Empowered in the first place.
  if (cost.disempowerSelf && !source.statuses.includes('empowered')) return false;
  // 440. Burning off an empty deck is not a price that can be met.
  if (cost.burn && zoneList(state, 'mainDeck', player).length < cost.burn) return false;
  if (cost.sacrifice && sacrificeCandidates(state, rules, source, cost.sacrifice).length
    < cost.sacrifice.count) return false;
  return true;
}


function payCostParts(
  state: GameState, rules: Rules, source: GameObject, player: PlayerId,
  cost: ActivationCost,
  /** 203.1. Filled with whatever the price consumed, for "the unit you exhausted". */
  record?: ObjectId[],
  /**
   * 356.2. Where the card being paid for is going.
   *
   * "You may play me to its battlefield" opened that destination *because* a
   * unit the price could name is standing there, so the price has to take one
   * from there -- otherwise the play is legal and the reason for it is not.
   */
  prefer?: Location | null,
): boolean {
  if (cost.exhaust && source.exhausted) return false;
  if (cost.discard
    && discardCandidates(state, rules, player, cost.discardFilter).length < cost.discard) {
    return false;
  }
  if (cost.recycleFromTrash
    && recycleCandidates(state, rules, player, cost.recycleFromTrashFilter)
      .length < cost.recycleFromTrash) return false;
  if (cost.spendXp && state.players[player].xp < cost.spendXp) return false;
  if (cost.spendBuff && !source.statuses.includes('buffed')) return false;

  if (cost.exhaust) ops.exhaust(state, source);
  if (cost.discard) {
    // Through the same list the affordability check used, or a filtered
    // discard could be checked against spells and then paid with anything.
    const payable = discardCandidates(state, rules, player, cost.discardFilter);
    ops.discard(state, rules, player, payable.slice(0, cost.discard));
  }
  if (cost.recycleFromTrash) {
    // Through the same list the affordability check used, or a filtered
    // recycle could be checked against units and then paid with anything.
    const payable = recycleCandidates(state, rules, player, cost.recycleFromTrashFilter);
    ops.recycle(state, rules, payable.slice(0, cost.recycleFromTrash));
  }
  if (cost.spendXp) state.players[player].xp -= cost.spendXp;
  if (cost.sacrifice) {
    // 203.3. Checked above, so the candidates are known to be there.
    const pool = sacrificeCandidates(state, rules, source, cost.sacrifice);
    // The destination first, then everything else: a preference, not a filter,
    // so a card whose destination holds no candidate still pays normally.
    const ordered = prefer
      ? [...pool].sort((a, b) =>
        Number(sameLocation(b.location, prefer)) - Number(sameLocation(a.location, prefer)))
      : pool;
    const victims = ordered.slice(0, cost.sacrifice.count);
    for (const victim of victims) {
      record?.push(victim.id);
      switch (cost.sacrifice.action) {
        case 'kill': ops.kill(state, rules, victim, source.id); break;
        case 'exhaust': ops.exhaust(state, victim); break;
        case 'return': ops.returnToHand(state, rules, victim); break;
        case 'spendBuff': ops.spendBuff(victim); break;
      }
    }
  }
  if (cost.spendBuff) {
    state.pendingTriggers.push({ on: 'youSpendBuff', player });
    ops.spendBuff(source);
  }
  if (cost.recycleSelf) ops.recycle(state, rules, [source.id]);
  if (cost.banishSelf) ops.banish(state, rules, source, source.id);
  // 440. Burn is a price paid off the top of the Main Deck.
  if (cost.burn) ops.burn(state, rules, player, cost.burn);
  // 441. Spending the Empowered status is what "Disempower this" charges.
  if (cost.disempowerSelf) ops.disempower(state, source);
  if (cost.killSelf) ops.kill(state, rules, source, source.id);
  return true;
}

/** 356.2. The extra cost a card asks for as it is played, if it has one. */
/**
 * 356.2. What a variable additional cost would actually spend.
 *
 * "Spend any number of buffs ... reduce my cost by [O] for each" is one thing:
 * the price and the discount move together. Nothing here can prompt, so the
 * play takes the fewest that make the card affordable -- spending more buys
 * nothing and costs the board a buff or a unit.
 *
 * Returns null when no count makes it affordable, which is how a card whose
 * mandatory extra cost cannot be met is refused.
 */
function variableSpend(
  state: GameState, rules: Rules, player: PlayerId, def: CardDef,
  extra: Extract<Ability, { kind: 'additionalCost' }>, cost: Cost,
): { count: number; victims: GameObject[] } | null {
  const variable = extra.variable;
  if (!variable) return null;
  const pool = state.players[player].pool;
  const available = boardObjects(state).filter((o) =>
    o.controller === player
    && isUnitType(rules.index.get(o.card).type)
    && (variable.of === 'buff' ? o.statuses.includes('buffed') : true));

  for (let n = 0; n <= available.length; n++) {
    const price = discounted(cost, {
      energy: variable.per.energy * n,
      power: variable.per.power * n,
    });
    if (canPay(pool, price, purposeOfCard(def.type), inShowdown(state))) {
      return { count: n, victims: available.slice(0, n) };
    }
  }
  return null;
}

function additionalCostOf(
  rules: Rules, cardId: string,
): Extract<Ability, { kind: 'additionalCost' }> | null {
  for (const a of abilitiesOf(rules, cardId)) {
    if (a.kind === 'additionalCost') return a;
  }
  return null;
}

/** 144. Exhaust units to move them to a shared destination. */
/**
 * 816. Whether something standing where `obj` is switches off a keyword's
 * effect there -- "your [Temporary] effects at my battlefield don't trigger".
 */
function suppressedAt(
  state: GameState, rules: Rules, obj: GameObject, keyword: Keyword,
): boolean {
  const at = obj.location;
  if (!at) return false;
  return boardObjects(state).some((o) =>
    sameLocation(o.location, at)
    && activePassives(state, rules, o).some(
      (e) => e.p === 'suppressKeyword' && e.keyword === keyword));
}

/** 144.3. Whether anything standing at this battlefield opens it to any move. */
function openToMoves(state: GameState, rules: Rules, index: number): boolean {
  const at = battlefieldLocation(index);
  return boardObjects(state).some((o) =>
    sameLocation(o.location, at)
    && activePassives(state, rules, o).some((e) => e.p === 'openToMoves'));
}

/**
 * 144.3. What a group move to this destination costs beyond exhausting.
 *
 * The standard move is otherwise free, so this is the only price on it. It is
 * charged per unit past the first, which is what makes it a tax on moving
 * *together* rather than a tax on moving.
 */
function moveTax(
  state: GameState, rules: Rules, player: PlayerId, to: Location, count: number,
): Cost | null {
  if (count < 2 || to.kind !== 'battlefield') return null;
  let energy = 0;
  let power = 0;
  for (const o of boardObjects(state)) {
    if (!sameLocation(o.location, to)) continue;
    for (const e of activePassives(state, rules, o)) {
      if (e.p !== 'moveTax') continue;
      if (e.who === 'opponents' && o.controller === player) continue;
      energy += e.energy * (count - 1);
      power += e.power * (count - 1);
    }
  }
  return energy === 0 && power === 0
    ? null
    : { energy, power, powerDomains: [] };
}

/**
 * 144.4. Whether this one unit may take its standard move to this destination.
 *
 * The enumerator and `doMove` both have to answer this, and they used to
 * answer it separately: `standardMoves` applied 144.4's destination rules
 * while `doMove` checked only that the unit was the player's, ready and not
 * held in place. That gap was not reachable through the enumerated list, but
 * it is reachable now that a group is *composed* rather than picked off it --
 * without this, a composed move could walk a unit from one battlefield to
 * another with no Ganking, or into the opponent's base.
 */
function canStandardMove(
  state: GameState, rules: Rules, unit: GameObject, to: Location, player: PlayerId,
): boolean {
  const from = unit.location;
  if (!from || sameLocation(from, to)) return false;
  if (unit.controller !== player || unit.exhausted) return false;
  if (!isUnitType(rules.index.get(unit.card).type)) return false;
  if (cantBeMovedBy(state, rules, unit, player)) return false;

  if (to.kind === 'battlefield') {
    // 144.4.a. Base to battlefield. From another battlefield it takes Ganking
    // (810.1.b), or a battlefield that opens itself to any move.
    if (from.kind === 'battlefield'
      && !hasKeywordNow(state, rules, unit, 'ganking')
      && !openToMoves(state, rules, to.index)) return false;
    return true;
  }
  // 144.4.b. Battlefield to *its own* base, and nowhere else.
  if (to.kind !== 'base' || to.player !== player) return false;
  if (from.kind !== 'battlefield') return false;
  return !isRestricted(state, rules, 'moveToBase', player, { subject: unit, at: from });
}

function standardMoves(state: GameState, rules: Rules, player: PlayerId): Action[] {
  const out: Action[] = [];
  const units = unitsOf(state, rules.index, player).filter((u) => !u.exhausted);
  // 144.3. "Players may perform multiple units' standard move simultaneously."
  // Collected per destination as the moves are enumerated, so the group offer
  // is built from exactly the units that were individually legal.
  const together = new Map<string, { to: Location; ids: ObjectId[] }>();

  for (const unit of units) {
    for (const bf of state.battlefields) {
      const to = battlefieldLocation(bf.index);
      if (!canStandardMove(state, rules, unit, to, player)) continue;
      out.push({ t: 'move', objects: [unit.id], to });
      const key = `${bf.index}`;
      const group = together.get(key);
      if (group) group.ids.push(unit.id);
      else together.set(key, { to, ids: [unit.id] });
    }
    const home = baseLocation(player);
    if (canStandardMove(state, rules, unit, home, player)) {
      out.push({ t: 'move', objects: [unit.id], to: home });
    }
  }

  // 144.3.a. One game action on several units, all arriving at the same place.
  //
  // Every singleton and the whole set are offered; the subsets in between are
  // not, because enumerating them multiplies the action list by 2^n. They are
  // still *legal*, and `doMove` validates a submitted group unit by unit
  // rather than by matching this list -- which is what lets a player compose
  // "these three of my five". The comment here used to say the subsets were
  // reachable by moving one at a time, and that was simply wrong: the first
  // move ends in a cleanup (453) that stages the combat, and 144.1.c forbids a
  // standard move during one. Moving one unit into a contested battlefield
  // committed it alone and locked the rest of the army out of the fight.
  for (const group of together.values()) {
    if (group.ids.length < 2) continue;
    const tax = moveTax(state, rules, player, group.to, group.ids.length);
    if (tax && !canPay(state.players[player].pool, tax, 'other', inShowdown(state))) continue;
    out.push({ t: 'move', objects: [...group.ids], to: group.to });
  }
  return out;
}

// -- applying an action ------------------------------------------------------

export function applyAction(state: GameState, rules: Rules, action: Action): boolean {
  const w = state.waiting;
  if (w.kind === 'gameOver' || w.kind === 'none') return false;

  switch (action.t) {
    case 'mulligan': {
      if (w.kind !== 'mulligan') return false;
      return doMulligan(state, rules, w.player, action.cards);
    }

    case 'choose': {
      if (w.kind !== 'choose') return false;
      // Re-checked against the same list the client was offered: an answer
      // that is not on it must not reach the resolver.
      if (!choiceOptions(state).some((a) => a.t === 'choose' && a.pick === action.pick)) {
        return false;
      }
      // Two suspensions can be waiting at once, so the answer is filed where
      // the question came from.
      if (w.kind === 'choose' && state.pendingChoice?.for === 'trigger') {
        state.triggerChoiceLog.push(action.pick);
      } else {
        state.choiceLog.push(action.pick);
      }
      state.pendingChoice = null;
      // Back to resolving. The chain still holds the item, unfinalized and
      // untouched, so `advance` picks it up again and replays it with the
      // answer in the log.
      state.waiting = { kind: 'none' };
      state.version++;
      return true;
    }

    case 'concede': {
      const player = 'player' in w ? w.player : state.turnPlayer;
      state.players[player].hasConceded = true;
      state.winner = OPPONENT(player);
      state.waiting = { kind: 'gameOver' };
      state.events.push({ t: 'gameOver', winner: state.winner, reason: 'concede' });
      return true;
    }

    case 'assignDamage': {
      if (w.kind !== 'assignDamage') return false;
      return doAssignDamage(state, rules, action.order);
    }

    case 'pass': {
      if (w.kind !== 'priority') return false;
      return doPass(state, rules);
    }

    case 'endTurn': {
      if (w.kind !== 'priority' || w.player !== state.turnPlayer) return false;
      if (state.phase !== 'main' || state.chain.length > 0 || state.combat) return false;
      state.phase = 'ending';
      state.priority = null;
      state.waiting = { kind: 'none' };
      return true;
    }

    case 'play':
      if (w.kind !== 'priority') return false;
      return doPlay(state, rules, w.player, action);

    case 'activate':
      if (w.kind !== 'priority') return false;
      return doActivate(state, rules, w.player, action);

    case 'move':
      if (w.kind !== 'priority') return false;
      return doMove(state, rules, w.player, action);

    case 'hide':
      if (w.kind !== 'priority') return false;
      return doHide(state, rules, w.player, action);
  }
}

function doMulligan(
  state: GameState, rules: Rules, player: PlayerId, cards: ObjectId[],
): boolean {
  if (cards.length > state.config.mulliganLimit) return false;
  const hand = zoneList(state, 'hand', player);
  if (!cards.every((id) => hand.includes(id))) return false;

  // 117.2-3. Draw replacements first, then recycle the cards set aside.
  const setAside = [...cards];
  for (const id of setAside) {
    const i = hand.indexOf(id);
    if (i >= 0) hand.splice(i, 1);
  }
  ops.draw(state, rules, player, setAside.length);
  ops.recycle(state, rules, setAside);
  // 117.3 recycles, and 416.1 puts a recycled card on the *bottom* of the
  // deck. It used to shuffle the whole deck afterwards, which is in no rule
  // and undoes the only thing recycling does: a card thrown away in the
  // mulligan went straight back into the middle of the deck and could be drawn
  // again a turn later. Reported by a player who mulliganed their single copy
  // of a card and saw it again immediately.
  //
  // The draw comes first for the same reason (117.2 before 117.3): the
  // replacements are dealt while the set-aside cards are still out of the
  // deck, so a mulliganed card can never be its own replacement.
  state.events.push({ t: 'mulligan', player, count: setAside.length });

  state.waiting = player === 0 ? { kind: 'mulligan', player: 1 } : { kind: 'none' };
  return true;
}

function doPass(state: GameState, rules: Rules): boolean {
  state.passes++;
  state.waiting = { kind: 'none' };

  if (state.chain.length > 0) {
    state.priority = OPPONENT(state.priority ?? state.turnPlayer);
    return true;
  }

  if (state.showdownState === 'showdown') {
    // 347.2. Both players passing in sequence closes the showdown.
    if (state.passes < 2 && state.focus !== null) {
      state.focus = OPPONENT(state.focus);
      state.priority = state.focus;
      state.events.push({ t: 'focus', player: state.focus });
    }
    return true;
  }

  // 305. Passing with nothing on the chain in the main phase ends the turn.
  if (state.phase === 'main') state.phase = 'ending';
  state.priority = null;
  void rules;
  return true;
}

function doPlay(
  state: GameState, rules: Rules, player: PlayerId, action: Extract<Action, { t: 'play' }>,
): boolean {
  const card = state.objects[action.object];
  if (!card) return false;

  // 811.1.b. A facedown card is played from where it lies, ignoring its base
  // cost, from the turn after it was hidden.
  const hiddenAt = card.zone.kind === 'facedown' ? card.zone.battlefield : null;
  const fromHidden = hiddenAt !== null;
  if (hiddenAt !== null) {
    if (card.controller !== player || card.enteredTurn >= state.turn) return false;
    const bf = state.battlefields[hiddenAt];
    if (!bf || bf.controller !== player) return false;
  } else {
    // 829.1.b. A Flow spell is played from the trash; everything else comes
    // from hand or the Champion Zone.
    const from = card.zone;
    const altHere = action.alt === true ? scriptFor(rules, card.card).altCost : undefined;
    const allowed = action.flow === true || action.fromTrashOffer === true
      ? from.kind === 'trash'
      : altHere
        ? from.kind === altHere.from
        : from.kind === 'hand' || from.kind === 'championZone';
    if (!allowed || from.kind === 'board' || from.kind === 'chain'
      || from.kind === 'facedown') return false;
    if (from.player !== player) return false;
    if (action.flow === true && !scriptFor(rules, card.card).flowCost) return false;
    // 356.4. The offer is re-checked rather than trusted: it is a turn effect,
    // and it can be spent or gone by the time the play is taken.
    if (action.fromTrashOffer === true && !trashOffer(state, card, player)) return false;
    // The gate is re-tested here, not trusted from the enumeration: the board
    // can have changed between offering the play and taking it.
    if (action.alt === true
      && (!altHere
        || (altHere.cond
          && testStaticCondition(state, rules, card, player, altHere.cond) !== true))) {
      return false;
    }
    if (!canPlayNow(state, rules, card, player)) return false;
  }

  const def = rules.index.get(card.card);
  const accelerate = action.accelerate === true
    && def.keywords.includes('accelerate') && isUnitType(def.type);
  // 829.1.b. Played from the trash for its Flow cost, which replaces the
  // printed one rather than adding to it.
  const flow = action.flow === true ? scriptFor(rules, card.card).flowCost : undefined;
  // 356.4. The alternative price replaces the printed one the same way Flow's
  // does; only the permission around it differs.
  const alt = action.alt === true ? scriptFor(rules, card.card).altCost : undefined;
  // 356.4. The granted price replaces the printed one, the same way Flow's and
  // the alternative price do.
  const offered = action.fromTrashOffer === true ? trashOffer(state, card, player) : null;
  let flowDiscount = 0;
  if (flow) {
    for (const o of boardObjects(state)) {
      if (o.controller !== player) continue;
      for (const e of activePassives(state, rules, o)) {
        if (e.p !== 'flowCostModifier') continue;
        flowDiscount += e.energy;
        if (e.min !== undefined) flowDiscount = Math.min(flowDiscount, flow.energy - e.min);
      }
    }
  }
  let cost = offered
    ? {
      energy: offered.price.energy,
      power: offered.price.power,
      powerDomains: offered.price.domain && offered.price.domain !== 'any'
        ? [offered.price.domain] : def.domains,
    }
    : flow
    ? {
      energy: Math.max(0, flow.energy - flowDiscount),
      power: flow.power,
      powerDomains: flow.domain && flow.domain !== 'any' ? [flow.domain] : def.domains,
    }
    : alt
      ? {
        energy: alt.energy,
        power: alt.power,
        powerDomains: alt.domain && alt.domain !== 'any' ? [alt.domain] : def.domains,
      }
      : accelerate
      ? accelerateCost(state, rules, def, player)
      : totalCost(state, rules, def, player);
  // 809. The Deflect tax the chosen targets impose. Priced from the targets on
  // the action rather than re-derived, or the enumeration and the payment can
  // disagree about what this play chooses.
  // 820. The Repeat cost rides on top of everything else, and only a spell
  // that prints one may claim it.
  const repeat = action.repeat === true
    ? (repeatCostFor(state, rules, card, player) ?? undefined) : undefined;
  if (action.repeat === true && (!repeat || !isSpellType(def.type))) return false;
  if (repeat) {
    cost = { ...cost, energy: cost.energy + repeat.energy, power: cost.power + repeat.power };
  }
  cost = withDeflect(cost, ignoresDeflect(rules, card.card)
    ? 0 : deflectCost(state, rules, action.targets ?? [], player));
  // 356. And the discount the chosen objects hand back, priced from the same
  // target list so the enumeration and the payment cannot disagree.
  cost = discounted(cost,
    chosenDiscount(state, rules, action.targets ?? [], player, cost));
  // 356. And a discount the card itself carries, conditional on what this play
  // chose -- settled here for the same reason, that targets now exist.
  cost = discounted(cost,
    choosingDiscount(state, rules, card.card, action.targets ?? [], card));
  // 356. A once-a-turn discount on plays, spent by whichever matching card
  // goes first this turn.
  cost = discounted(cost, {
    energy: firstEachTurnDiscount(state, rules, player, 'card', def),
    power: 0,
  });
  // 356. "I cost [2] less to play from anywhere other than your hand."
  if (card.zone.kind !== 'hand') {
    cost = discounted(cost, nonHandDiscount(rules, card.card));
  }
  // 356. "The next spell you play this turn costs [5] less" -- spent by the
  // first spell that uses it, the same way the enters-ready one is. A card that
  // says "your next *card*" is not restricted to spells.
  if (isSpellType(def.type)
    || state.turnEffects.some((e) =>
      e.k === 'nextSpellDiscount' && e.player === player && e.anyCard)) {
    const i = state.turnEffects.findIndex(
      (e) => e.k === 'nextSpellDiscount' && e.player === player,
    );
    const armed = i >= 0 ? state.turnEffects[i] : undefined;
    if (armed?.k === 'nextSpellDiscount') {
      state.turnEffects.splice(i, 1);
      cost = discounted(cost, { energy: armed.energy, power: armed.power ?? 0 });
    }
  }

  // 356.2. An additional cost is settled before the base cost, so a card whose
  // extra cost cannot be paid is never played at all. A mandatory one must be
  // paid; an optional one is taken only when the action asks for it.
  const extra = additionalCostOf(rules, card.card);
  let paidAdditional = false;
  let continueAfterExtra = false;
  if (extra) {
    const wants = !extra.optional || action.additional === true;
    if (wants) {
      const resource = {
        energy: extra.cost.energy ?? 0,
        power: extra.cost.power ?? 0,
        powerDomains: extra.cost.powerDomain && extra.cost.powerDomain !== 'any'
          ? [extra.cost.powerDomain === 'self'
            ? (def.domains[0] ?? 'fury') : extra.cost.powerDomain]
          : [],
      };
      // 356.2. "Optional additional costs you pay cost [1] less."
      const off = extraCostDiscount(state, rules, player, resource);
      resource.energy = Math.max(0, resource.energy - off.energy);
      resource.power = Math.max(0, resource.power - off.power);
      // 356.2. A variable extra spends as many as it takes and discounts by
      // the same count, so the two can never disagree.
      if (extra.variable) {
        const spend = variableSpend(state, rules, player, def, extra, cost);
        if (!spend) return false;
        for (const victim of spend.victims) {
          if (extra.variable.of === 'buff') {
            ops.spendBuff(victim);
          } else {
            ops.kill(state, rules, victim, card.id);
          }
        }
        cost = discounted(cost, {
          energy: extra.variable.per.energy * spend.count,
          power: extra.variable.per.power * spend.count,
        });
        paidAdditional = true;
        continueAfterExtra = true;
      }
      if (!continueAfterExtra) {
        if (!canPay(state.players[player].pool, resource)) return false;
        // 356.2. What the price actually consumed, so a discount measured
        // against it reads the same card the payment took.
        const taken: ObjectId[] = [];
        if (!payCostParts(state, rules, card, player, extra.cost, taken, action.to ?? null)) {
          return false;
        }
        pay(state.players[player].pool, resource);
        paidAdditional = true;
        cost = discounted(cost, sacrificedDiscount(
          rules, card.card,
          taken.map((id) => state.objects[id]).filter((o): o is GameObject => !!o),
        ));
      }
      // 356.2. "If you do, reduce my cost by [2]" -- settled here, after the
      // choice that decides it and before the base cost is paid.
      cost = discounted(cost, additionalCostDiscount(rules, card.card));
    }
  }

  // 356.2. The offer is re-read rather than trusted from the action: the
  // permanent making it can be gone by the time the play is taken.
  if (action.grantedBy !== undefined) {
    const offer = grantedPlayOffers(state, rules, card, player)
      .find((o) => o.by.id === action.grantedBy);
    if (!offer || !sameLocation(offer.to, action.to ?? null)) return false;
    cost = {
      energy: cost.energy + offer.cost.energy,
      power: cost.power + offer.cost.power,
      powerDomains: cost.powerDomains,
    };
  }

  if (!fromHidden && !pay(state.players[player].pool, cost,
    purposeOfCard(def.type), inShowdown(state))) return false;
  state.players[player].spentThisTurn.energy += cost.energy;
  state.players[player].spentThisTurn.power += cost.power;

  // 349. Where it came from, noted before it leaves: once it is on the chain
  // the original zone is gone.
  card.playedFrom = card.zone.kind;

  // 354. The card moves to the chain, which closes the state.
  moveToZone(state, rules, card, { kind: 'chain' });
  const item: ChainItem = {
    id: state.nextChainId++,
    kind: isSpellType(def.type) ? 'spell' : 'permanent',
    source: card.id, sourceCard: card.card, controller: player,
    finalized: false, targets: action.targets ?? [],
    destination: action.to ?? null, paidAccelerate: accelerate,
    paidAdditional: paidAdditional,
    ...(flow ? { paidFlow: true } : {}),
    ...(repeat ? { paidRepeat: true } : {}),
  };
  // 356.4. The offer is spent on the way to the chain, not on resolution: the
  // spell lands back in the trash when it finishes, and an unspent offer would
  // let it be replayed for the rest of the turn.
  if (offered) state.turnEffects = state.turnEffects.filter((e) => e !== offered);
  noteChoices(state, player, action.targets ?? []);
  state.chain.push(item);
  state.openState = 'closed';
  state.players[player].cardsPlayedThisTurn++;
  state.players[player].playedThisTurn.push(card.card);
  state.pendingTriggers.push({ on: 'nthCardPlayed', player });
  // 349. A spell is played and never arrives anywhere, so this is the only
  // moment it can be seen being played.
  //
  // `playerPlaysCard` used to be raised in `enterBoard` alone, which only a
  // permanent ever reaches -- so all thirteen cards reading "when you play a
  // spell" watched for an event a spell could not raise, and sat inert.
  // Ravenbloom Student's "+1 [M] this turn" simply never happened.
  //
  // A permanent still raises it as it enters the board rather than here, and
  // that is deliberate: Valley of Idols offers to [Buff] the unit that was
  // just played there and Star Spring moves another unit to it, both filtered
  // `where: 'here'` -- and a card on the chain is nowhere, so raising it at
  // play time would leave those two watching a unit with no location and
  // matching nothing. The moment a permanent is *played* and the moment it can
  // be *acted on* are different moments; a spell has only the one.
  if (isSpellType(def.type)) {
    state.pendingTriggers.push({ on: 'playerPlaysCard', player, subject: card.id });
  }
  state.passes = 0;
  state.priority = null;
  state.waiting = { kind: 'none' };
  state.version++;
  return true;
}

function doActivate(
  state: GameState, rules: Rules, player: PlayerId, action: Extract<Action, { t: 'activate' }>,
): boolean {
  const source = state.objects[action.object];
  if (!source || source.controller !== player || source.zone.kind !== 'board') return false;

  const ability = abilitiesOn(state, rules, source)
    .find((a): a is Extract<Ability, { kind: 'activated' }> =>
      a.kind === 'activated' && a.key === action.ability);
  if (!ability) return false;
  if (ability.useIf
    && testStaticCondition(state, rules, source, player, ability.useIf) !== true) return false;

  // Pay the cost (203): every component must be payable or none of it happens.
  const pool = state.players[player].pool;
  const resourceCost = abilityResourceCost(
    state, rules, source, ability.cost, ability.from, action.targets ?? [],
  );
  const abilityPurpose = purposeOfAbility(rules.index.get(source.card).type);
  if (!canPay(pool, resourceCost, abilityPurpose, inShowdown(state))) return false;
  // Through the same checker the additional-cost path uses, so a cost
  // component can never be honoured in one place and ignored in the other.
  if (!canPayCostParts(state, rules, source, player, ability.cost)) return false;

  // 371. Committed only now: an activation that failed to pay does not use up
  // the turn's one allowance.
  if (ability.once) source.usedThisTurn.push(ability.key);

  pay(pool, resourceCost, abilityPurpose, inShowdown(state));
  state.players[player].spentThisTurn.energy += resourceCost.energy;
  state.players[player].spentThisTurn.power += resourceCost.power;
  // 203.1. Kept so the effects can name what the price consumed.
  const paidWith: ObjectId[] = [];
  payCostParts(state, rules, source, player, ability.cost, paidWith);

  // 414. The subject is the permanent whose ability was used, so "of a gear"
  // is an ordinary filter on it.
  state.pendingTriggers.push({ on: 'youUseAbility', player, subject: source.id });
  state.players[player].abilitiesUsedThisTurn.push(source.card);

  noteChoices(state, player, action.targets ?? []);

  const item: ChainItem = {
    id: state.nextChainId++, kind: 'ability', source: source.id,
    sourceCard: source.card, controller: player, finalized: false,
    targets: action.targets ?? [], destination: null, paidAccelerate: false,
    paidAdditional: false,
    effects: ability.effects,
    addsResources: onlyAddsResources(ability.effects),
    ability: ability.key,
    ...(paidWith.length ? { paidWith } : {}),
  };
  state.chain.push(item);
  state.openState = 'closed';
  state.passes = 0;
  state.priority = null;
  state.waiting = { kind: 'none' };
  state.version++;
  return true;
}

function doMove(
  state: GameState, rules: Rules, player: PlayerId, action: Extract<Action, { t: 'move' }>,
): boolean {
  if (state.phase !== 'main' || state.openState !== 'open') return false;
  if (state.showdownState !== 'neutral' || player !== state.turnPlayer) return false;

  // 144.3. A group is checked unit by unit against the same test that
  // enumerated the singletons, rather than by matching the offered list. That
  // is what makes any subset submittable -- the player composes "these three
  // of my five" and the engine still cannot be talked into an illegal move,
  // because every unit in the set had to pass on its own.
  const seen = new Set<ObjectId>();
  const units = action.objects
    .map((id) => state.objects[id])
    .filter((u): u is GameObject => {
      if (!u || seen.has(u.id)) return false;
      seen.add(u.id);
      return canStandardMove(state, rules, u, action.to, player);
    });
  if (units.length !== action.objects.length || units.length === 0) return false;

  // 144.3.c. The costs are paid simultaneously, before any unit moves -- a
  // group that cannot afford the tax does not move at all.
  const tax = moveTax(state, rules, player, action.to, units.length);
  if (tax && !pay(state.players[player].pool, tax)) return false;

  for (const unit of units) {
    // 144.2. Exhausting the unit is the cost of the standard move.
    ops.exhaust(state, unit);
    ops.move(state, unit, action.to, player);
    fireSelfTriggers(state, rules, unit, { on: 'moves' });
    state.pendingTriggers.push({
      on: 'opponentMoves', player: OPPONENT(unit.controller), subject: unit.id,
    });
  }

  // 450. A battlefield the mover does not control becomes contested.
  if (action.to.kind === 'battlefield') {
    const bf = battlefield(state, action.to.index);
    if (bf.controller !== player && bf.contestedBy === null) {
      bf.contestedBy = player;
      state.events.push({ t: 'contest', battlefield: bf.index, by: player });
    }
  }

  state.waiting = { kind: 'none' };
  state.version++;
  return true;
}

function doAssignDamage(state: GameState, rules: Rules, order: ObjectId[]): boolean {
  const pending = state.pendingAssignment;
  const combat = state.combat;
  if (!pending || !combat) return false;
  if (!isLegalDamageOrder(state, rules, order)) return false;

  const assigned = assignmentFor(state, rules, order, pending.total);
  state.assignments.push(...assigned);

  // 705.4. Anything past what was lethal on a unit is excess. Counted as it is
  // assigned so it can be credited to the player who assigned it.
  let excess = 0;
  for (const a of assigned) {
    const target = state.objects[a.object];
    if (!target) continue;
    const lethal = Math.max(1, effectiveMight(state, rules, target) - target.damage);
    excess += Math.max(0, a.amount - lethal);
  }
  state.players[pending.player].excessDamageThisTurn += excess;

  const loc = battlefieldLocation(combat.battlefield);
  if (pending.player === combat.attacker) {
    // The defender assigns next, using their own summed Might.
    const defenders = unitsOf(state, rules.index, combat.defender, loc);
    const attackers = unitsOf(state, rules.index, combat.attacker, loc);
    state.pendingAssignment = {
      player: combat.defender,
      total: sumMight(state, rules, defenders),
      targets: attackers.map((a) => a.id),
    };
    state.waiting = { kind: 'none' };
    return true;
  }

  // 465.2.c.1.a. Both sides assigned; now deal it all at once.
  for (const a of state.assignments) {
    const target = state.objects[a.object];
    if (target && canBeDealtDamage(state, rules, target)) {
      ops.deal(state, target, a.amount, null);
    }
  }
  state.events.push({
    t: 'combatDamage', battlefield: combat.battlefield, assignments: [...state.assignments],
  });
  state.pendingAssignment = null;
  state.assignments = [];
  combat.step = 'resolution';
  state.waiting = { kind: 'none' };
  return true;
}

export { isSpellType, isPermanentType, canPay, pay, totalCost, keywordAmount };
